import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminLayout from "../../components/layout/AdminLayout";
import { adminApi } from "../../api";
import ImageUploadField from "../../components/ImageUploadField";
import AdminSelect from "../../components/AdminSelect";
import MultiSelect from "../../components/MultiSelect";
import ColorPicker from "../../components/ColorPicker";
import DigitalCardTab from "../../components/DigitalCardTab";
import "../../components/DigitalCardTab.css";
import FoxidConfigTab from "../../components/FoxidConfigTab";
import IgtTunnelPanel from "../../components/IgtTunnelPanel";
import S7000TunnelPanel from "../../components/S7000TunnelPanel";
import AnalysisCodesTab from "../../components/AnalysisCodesTab";
import { SortableHeader, PaginationBar, FilterSelect } from "../../components/DataTable";
import timeAgo from "../../utils/timeAgo";
import { formatDateAU, formatDateTimeAU } from "../../utils/formatDate";
import { SIGNUP_TEXT_SLOTS, FONT_OPTIONS } from "../../utils/signupTextStyle";
import SignupLivePreview from "../../components/SignupLivePreview";
import SignupBenefitsEditor from "../../components/SignupBenefitsEditor";
import SignupLayoutControls from "../../components/SignupLayoutControls";
import FaceMatchBadge from "../../components/FaceMatchBadge";
import IdentityVerificationCard from "../../components/IdentityVerificationCard";
import EmailsTab from "../../components/EmailsTab";
import EmailProviderPanel from "../../components/EmailProviderPanel";
import PasswordInput from "../../components/PasswordInput";
import { hideableMenusForProvider, MENU_GROUP_LABELS } from "../../config/clientMenus";
import CsvMemberImportPanel from "../../components/CsvMemberImportPanel";
import VenueModeToggle from "../../components/VenueModeToggle";
import EnvironmentSubTabs from "../../components/EnvironmentSubTabs";
import ApiKeysTab from "../../components/ApiKeysTab";
import IntegrationTestsTab from "../../components/IntegrationTestsTab";
import useUrlState from "../../hooks/useUrlState";

/* ── Tiny SVG icon helpers ─────────────────── */
const Icon = {
  settings: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  members: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  logs: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  clients: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  products: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>,
  general: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  branding: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>,
  provider: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><circle cx="6" cy="6" r="1"/><circle cx="6" cy="18" r="1"/></svg>,
  stripe: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
  copy: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><polyline points="20 6 9 17 4 12"/></svg>,
  eye: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  eyeOff: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>,
  externalLink: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
  zap: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  wallet: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M16 14h.01"/><path d="M2 10h20"/></svg>,
  mail: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
};

/* ── Password-toggling input ───────────────── */
function SecretInput({ value, onChange, placeholder, ...rest }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="secret-input-wrap">
      <input type={visible ? "text" : "password"} value={value} onChange={onChange} placeholder={placeholder} {...rest} />
      <button type="button" className="secret-toggle" onClick={() => setVisible(!visible)} tabIndex={-1} aria-label={visible ? "Hide" : "Show"}>
        {visible ? Icon.eyeOff : Icon.eye}
      </button>
    </div>
  );
}

/* ── Copy-to-clipboard helper ──────────────── */
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handle = () => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return <button type="button" className="btn-icon-mini" onClick={handle} title="Copy">{copied ? Icon.check : Icon.copy}</button>;
}

/**
 * One editable signup text line: show/hide toggle, the text itself, and an
 * optional per-line font + colour. Blank font/colour inherit the venue-wide
 * Font Family / Text colour, so a venue only overrides what it cares about.
 */
function SignupTextRow({ slot, value, onValue, style, onStyle, placeholder }) {
  const enabled = style.enabled !== false;
  const set = (patch) => onStyle({ ...style, ...patch });
  return (
    <div className="form-field" style={{ border: "1px solid var(--border)", borderRadius: 10, padding: "0.75rem", marginBottom: "0.6rem", opacity: enabled ? 1 : 0.55 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", marginBottom: "0.4rem" }}>
        <label style={{ margin: 0 }}>
          {slot.label} <span className="field-hint-inline">{slot.group}</span>
        </label>
        <label className="toggle-switch" style={{ cursor: "pointer" }} title={enabled ? "Shown on the signup form" : "Hidden"}>
          <input type="checkbox" checked={enabled} onChange={(e) => set({ enabled: e.target.checked })} />
          <span className="toggle-track" />
        </label>
      </div>
      <input value={value} onChange={(e) => onValue(e.target.value)} placeholder={placeholder} disabled={!enabled} />
      <span className="field-hint">{slot.hint}</span>
      <div className="form-grid-2" style={{ marginTop: "0.5rem" }}>
        <div className="form-field" style={{ margin: 0 }}>
          <label style={{ fontSize: "0.78rem" }}>Font <span className="optional">— inherit</span></label>
          <AdminSelect
            value={style.font || ""}
            onChange={(v) => set({ font: v || undefined })}
            options={[{ value: "", label: "Use venue font" }, ...FONT_OPTIONS.map((f) => ({ value: f, label: f }))]}
            placeholder="Use venue font"
            disabled={!enabled}
          />
        </div>
        <div className="form-field" style={{ margin: 0 }}>
          <label style={{ fontSize: "0.78rem" }}>Colour <span className="optional">— inherit</span></label>
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <ColorPicker value={style.color || ""} onChange={(v) => set({ color: v || undefined })} compact />
            {style.color && (
              <button type="button" className="btn btn-outline btn-sm" onClick={() => set({ color: undefined })}>Reset</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Toggle row for the CircleScan reference-data modal. Module scope on purpose —
 * declared inside a render callback its identity changes every render, so React
 * remounts the input (killing the slide animation and stealing focus). */
function ToggleRow({ id, label, hint, checked, onChange }) {
  return (
    <label className="toggle-row" htmlFor={id}>
      <div className="toggle-row-text">
        <strong>{label}</strong>
        {hint && <span>{hint}</span>}
      </div>
      <div className="toggle-switch">
        <input type="checkbox" id={id} checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
        <span className="toggle-track" />
      </div>
    </label>
  );
}

/* Members tab — reads from the cached members endpoint
 * (`/admin/venues/:id/cached-members`) so imported / synced members
 * appear here, not just portal-managed ones. Supports sorting,
 * filtering by status / source, and pagination. Strictly
 * venue-scoped by the backend query. */
function AdminMembersTab({ venueId, providerType }) {
  const providerLabel = providerType === "igt" ? "IGT" : providerType === "swiftpos" ? "SwiftPOS" : providerType === "circlescan" ? "CircleScan" : "S7000";
  const supportsCache = true; // all providers now back the Members list with a cache table
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [portalCount, setPortalCount] = useState(0);
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("all");        // "all" | "portal"
  const [status, setStatus] = useState("all");      // "all" | "active" | "suspended"
  // Newest members first — the most useful default when checking recent signups.
  const [sort, setSort] = useState({ by: "dateregistered", dir: "desc" });
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  // Reset offset when filters change (not when offset itself does).
  useEffect(() => { setOffset(0); }, [q, scope, status, sort.by, sort.dir, limit]);

  useEffect(() => {
    let alive = true;
    const handle = setTimeout(async () => {
      setLoading(true); setError("");
      try {
        const d = await adminApi.cachedMembers(venueId, {
          q, scope, status,
          sort_by: sort.by, sort_dir: sort.dir,
          limit, offset,
        });
        if (!alive) return;
        setRows(d.members || []); setTotal(d.total || 0); setPortalCount(d.portal_managed || 0);
      } catch (err) { if (alive) setError(err.message); }
      if (alive) setLoading(false);
    }, 250);
    return () => { alive = false; clearTimeout(handle); };
  }, [venueId, q, scope, status, sort.by, sort.dir, limit, offset]);

  return (
    <div>
      <div className="venues-summary" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
        <div className="venues-summary-counts" style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700 }}>Members</h2>
          <span>
            {scope === "portal"
              ? <><strong>{total}</strong> portal-managed</>
              : <><strong>{total}</strong> in {providerLabel} · <strong>{portalCount}</strong> portal-managed</>}
          </span>
          <div style={{ display: "inline-flex", borderRadius: 6, overflow: "hidden", border: "1px solid var(--border)" }}>
            <button type="button" className={`btn btn-sm ${scope === "all" ? "btn-primary" : "btn-outline"}`} style={{ borderRadius: 0, border: "none" }} onClick={() => setScope("all")}>All {providerLabel}</button>
            <button type="button" className={`btn btn-sm ${scope === "portal" ? "btn-primary" : "btn-outline"}`} style={{ borderRadius: 0, border: "none" }} onClick={() => setScope("portal")}>Portal</button>
          </div>
          <FilterSelect
            label="Status"
            value={status}
            onChange={setStatus}
            options={[
              { value: "all", label: "All" },
              { value: "active", label: "Active only" },
              { value: "suspended", label: "Suspended only" },
            ]}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flex: 1, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <div className="search-box" style={{ flex: "0 1 320px" }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input placeholder="Search name, email, or member #…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {supportsCache && (
            <button
              type="button"
              className={`btn btn-sm ${showImport ? "btn-primary" : "btn-outline"}`}
              onClick={() => setShowImport((s) => !s)}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: "-2px" }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              {showImport ? "Hide import" : "Import members"}
            </button>
          )}
        </div>
      </div>

      {error && <div className="toast toast-error" style={{ margin: "0.75rem 0" }}>{error}</div>}

      {showImport && (
        <div style={{ margin: "1rem 0" }}>
          <CsvMemberImportPanel venueId={Number(venueId)} providerType={providerType} />
        </div>
      )}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <SortableHeader columnKey="surname" sort={sort} onSort={setSort}>Name</SortableHeader>
              <SortableHeader columnKey="membernumber" sort={sort} onSort={setSort}>Member #</SortableHeader>
              <SortableHeader columnKey="email" sort={sort} onSort={setSort}>Email</SortableHeader>
              <th>Mobile</th>
              <th>Status</th>
              <SortableHeader columnKey="dateregistered" sort={sort} onSort={setSort}>Joined</SortableHeader>
              <SortableHeader columnKey="synced_at" sort={sort} onSort={setSort}>Last synced</SortableHeader>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" style={{ textAlign: "center", padding: "2rem", color: "var(--text-muted)" }}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="8" style={{ textAlign: "center", padding: "2rem", color: "var(--text-muted)" }}>
                {providerType === "igt"
                  ? "No members cached yet. Use Import CSV above, or Import a single member from the Settings → Provider tab."
                  : providerType === "swiftpos"
                  ? "No members cached yet. New portal signups appear here automatically; use Import CSV above, or Import a single member by SwiftPOS id from the Settings → Provider tab."
                  : providerType === "circlescan"
                  ? "No members cached yet. New portal signups appear here automatically; run Sync from the Settings → Provider tab to pull the full CircleScan member list, or Import a single member by number."
                  : "No members found. Run a sync from the Settings → Provider tab if the cache is empty."}
              </td></tr>
            ) : rows.map((m) => (
              <tr
                key={m.membernumber}
                onClick={() => setSelectedMember(m)}
                style={{ cursor: "pointer" }}
                className="admin-member-row"
              >
                <td style={{ fontWeight: 600 }}>{m.firstname || ""} {m.surname || ""}</td>
                <td><code style={{ fontSize: "0.8rem", background: "var(--bg-elevated)", padding: "0.2rem 0.5rem", borderRadius: 4 }}>#{m.membernumber}</code></td>
                <td style={{ color: "var(--text-muted)", fontSize: "0.875rem" }}>{m.email || "—"}</td>
                <td style={{ color: "var(--text-muted)", fontSize: "0.875rem" }}>{m.mobile || "—"}</td>
                <td>{m.suspensionflag === "1" ? <span className="badge inactive">Suspended</span> : <span className="badge active">Active</span>}</td>
                <td style={{ color: "var(--text-muted)", fontSize: "0.8rem", whiteSpace: "nowrap" }}>{formatDateAU(m.dateregistered)}</td>
                <td style={{ color: "var(--text-muted)", fontSize: "0.8rem", whiteSpace: "nowrap" }}>{formatDateTimeAU(m.synced_at)}</td>
                <td>
                  {m.portal_managed
                    ? <span className="badge active" style={{ fontSize: "0.7rem" }}>Portal</span>
                    : <span className="badge" style={{ fontSize: "0.7rem", background: "var(--bg-elevated)", color: "var(--text-muted)" }}>{providerLabel}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PaginationBar
        total={total}
        limit={limit}
        offset={offset}
        onLimit={setLimit}
        onOffset={setOffset}
      />

      {selectedMember && (
        <AdminMemberDetailModal
          venueId={venueId}
          providerLabel={providerLabel}
          member={selectedMember}
          onClose={() => setSelectedMember(null)}
        />
      )}

      <style>{`
        .admin-member-row:hover { background: var(--bg-subtle); }

        /* ── Member modal ─────────────────────────────────── */
        .member-modal { max-width: 900px; }
        .member-modal-header { gap: 1rem; padding: 1.5rem 1.75rem 1.25rem; }
        .member-avatar {
          width: 52px; height: 52px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--accent), color-mix(in srgb, var(--accent) 60%, transparent));
          color: white;
          display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 1.15rem;
          flex-shrink: 0;
          box-shadow: 0 2px 8px color-mix(in srgb, var(--accent) 30%, transparent);
        }
        .member-modal-body { padding: 1.5rem 1.75rem 1.75rem; }
        .member-header-pills {
          display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap;
          margin-top: 0.4rem;
        }
        .member-num {
          font-size: 0.78rem; padding: 0.15rem 0.55rem;
          background: var(--bg-elevated); border-radius: 4px;
          color: var(--text-muted);
        }

        .pill {
          display: inline-block;
          padding: 0.15rem 0.6rem;
          border-radius: 999px;
          font-size: 0.72rem;
          font-weight: 500;
          border: 1px solid transparent;
        }
        .pill-ok    { background: var(--success-soft); color: var(--success); border-color: color-mix(in srgb, var(--success) 30%, transparent); }
        .pill-warn  { background: var(--warning-soft); color: var(--warning); border-color: color-mix(in srgb, var(--warning) 30%, transparent); }
        .pill-info  { background: var(--accent-soft); color: var(--accent); border-color: color-mix(in srgb, var(--accent) 25%, transparent); }
        .pill-muted { background: var(--bg-elevated); color: var(--text-muted); border-color: var(--border); }

        .mm-section { background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 1.15rem 1.3rem; margin-bottom: 1rem; }
        .mm-section:last-child { margin-bottom: 0; }
        .mm-section-head {
          display: flex; align-items: center; justify-content: space-between; gap: 0.75rem;
          padding-bottom: 0.7rem; margin-bottom: 0.95rem; border-bottom: 1px solid var(--border);
          font-size: 0.9rem; font-weight: 650; color: var(--text);
        }
        .mm-section-meta { font-weight: 400; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-muted); }
        .mm-subhead {
          font-size: 0.64rem; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.05em; color: var(--text-muted); margin-bottom: 0.6rem;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 1rem 1.6rem;
        }
        .info-card { padding: 0; background: none; border: none; }
        .info-card.is-accent .info-card-value { color: var(--accent); }
        .info-card-label { font-size: 0.64rem; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.2rem; font-weight: 700; }
        .info-card-value { font-size: 0.92rem; font-weight: 500; color: var(--text); word-break: break-word; }

        .mm-howitworks {
          padding: 0.7rem 0.9rem;
          background: var(--accent-soft);
          border: 1px solid color-mix(in srgb, var(--accent) 20%, transparent);
          border-radius: 6px;
          font-size: 0.82rem;
          color: var(--text);
          margin-bottom: 0.85rem;
          line-height: 1.55;
        }

        .mm-banner {
          padding: 0.6rem 0.85rem;
          border-radius: 6px;
          font-size: 0.85rem;
          margin-bottom: 0.85rem;
        }
        .mm-banner-warn {
          background: var(--warning-soft);
          border: 1px solid color-mix(in srgb, var(--warning) 35%, transparent);
          color: var(--text);
        }

        .mm-issue-actions { display: flex; gap: 0.6rem; align-items: center; margin-bottom: 0.6rem; }

        .mm-preview {
          margin: 0.5rem 0 0.85rem;
          border: 1px solid var(--border);
          border-radius: 8px;
          overflow: hidden;
        }
        .mm-preview-table { margin: 0; font-size: 0.85rem; }
        .mm-preview-table th { background: var(--bg-subtle); }
        .mm-preview-foot {
          padding: 0.55rem 0.85rem;
          font-size: 0.78rem;
          color: var(--text-muted);
          background: var(--bg-elevated);
          border-top: 1px solid var(--border);
        }

        .mm-loading { color: var(--text-muted); font-size: 0.88rem; padding: 0.5rem 0; }
        .mm-empty   { padding: 1rem; text-align: center; }

        .mm-pass-list { display: flex; flex-direction: column; gap: 0.5rem; }
        .mm-pass {
          display: grid;
          grid-template-columns: auto 1fr auto;
          gap: 0.85rem;
          align-items: center;
          padding: 0.7rem 0.9rem;
          border: 1px solid var(--border);
          border-radius: 8px;
          background: var(--bg-subtle);
        }
        .mm-pass.is-active   { background: color-mix(in srgb, var(--success-soft) 60%, var(--bg-subtle)); border-color: color-mix(in srgb, var(--success) 25%, var(--border)); }
        .mm-pass.is-inactive { opacity: 0.85; }
        .mm-pass-date { font-size: 0.82rem; color: var(--text-muted); margin-bottom: 0.3rem; }
        .mm-pass-links { display: flex; gap: 0.4rem; flex-wrap: wrap; }

        .wallet-pill {
          display: inline-flex; align-items: center; gap: 0.3rem;
          padding: 0.25rem 0.65rem;
          border-radius: 999px;
          font-size: 0.78rem; font-weight: 500;
          text-decoration: none;
          border: 1px solid var(--border);
          background: var(--bg-elevated);
          color: var(--text);
        }
        .wallet-pill:hover { background: var(--bg-surface); }
        .wallet-apple::before  { content: ""; }
        .wallet-google::before { content: ""; }
      `}</style>
    </div>
  );
}

/* Admin "click a member row" modal — profile + digital passes + issue/re-issue.
 *
 * Issue/re-issue flow (server side):
 *   1. Build memberData via litecard.buildMemberDataForCard(venueId, memberId)
 *      — local members table > venue cache > product join, all keyed by the
 *      member's portal id (member.local_id from the cached query).
 *   2. Fetch the venue's linked Litecard template (live, from Litecard).
 *   3. Map data into the template via the venue's saved field_mapping
 *      (Settings → Digital Card → Template & Mapping).
 *   4. Re-issue mode: deactivate every existing ACTIVE pass first (Litecard
 *      + DB), then POST a new pass to Litecard.
 *
 * The "Pass data preview" panel renders steps 1–3 client-side so admins
 * can verify the right values land in each template field before clicking
 * Issue. Pass actions are gated on portal_managed=true (no local id ⇒
 * step 1 has nothing to look up).
 */
function AdminMemberDetailModal({ venueId, providerLabel, member, onClose }) {
  const [passes, setPasses]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState("");
  const [feedback, setFeedback] = useState("");

  const [preview, setPreview]         = useState(null);
  const [previewLoading, setPLoading] = useState(false);
  const [previewError, setPError]     = useState("");
  const [showPreview, setShowPreview] = useState(false);

  // Full portal-side `members` row — fetched lazily because the cached
  // list view only carries the lean shape used for the members table.
  const [portalDetails, setPortalDetails] = useState(null);
  // Per-member activity: join_logs + filtered api_request_logs.
  const [memberLogs, setMemberLogs]       = useState(null);
  const [logsLoading, setLogsLoading]     = useState(false);
  const [activeTab, setActiveTab]         = useState("profile"); // profile | logs

  const isPortalManaged = !!member.local_id;
  const hasActive = passes.some((p) => (p.status || "").toUpperCase() === "ACTIVE");
  const buttonLabel = hasActive ? "Re-issue pass" : "Issue pass";
  const busyLabel   = hasActive ? "Re-issuing…"   : "Issuing…";

  const reload = useCallback(async () => {
    if (!isPortalManaged) { setLoading(false); return; }
    setLoading(true); setError("");
    try {
      const list = await adminApi.getMemberPasses(venueId, member.local_id);
      setPasses(Array.isArray(list) ? list : (list?.passes || []));
    } catch (err) { setError(err.message); }
    setLoading(false);
  }, [venueId, member.local_id, isPortalManaged]);

  const loadPreview = useCallback(async () => {
    if (!isPortalManaged) return;
    setPLoading(true); setPError("");
    try {
      const r = await adminApi.getMemberPassPreview(venueId, member.local_id);
      setPreview(r);
    } catch (err) { setPError(err.message); setPreview(null); }
    setPLoading(false);
  }, [venueId, member.local_id, isPortalManaged]);

  useEffect(() => { reload(); loadPreview(); }, [reload, loadPreview]);

  // Fetch the full portal members row (identity / address / ID / T&Cs)
  // and the per-member activity logs whenever the modal opens for a
  // portal-managed member. Both are best-effort; failures just render
  // empty sections.
  useEffect(() => {
    if (!isPortalManaged) return;
    adminApi.getMemberPortalDetails(venueId, member.local_id)
      .then(setPortalDetails)
      .catch(() => setPortalDetails(null));
  }, [venueId, member.local_id, isPortalManaged]);

  useEffect(() => {
    if (!isPortalManaged || activeTab !== "logs") return;
    if (memberLogs) return;
    setLogsLoading(true);
    adminApi.getMemberLogs(venueId, member.local_id)
      .then(setMemberLogs)
      .catch(() => setMemberLogs({ joinLogs: [], apiLogs: [] }))
      .finally(() => setLogsLoading(false));
    // eslint-disable-next-line
  }, [activeTab, isPortalManaged]);

  // ESC closes the modal
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function issue() {
    if (hasActive && !window.confirm(
      "Re-issue creates a new pass and deactivates the current active one in Apple/Google Wallet. Members who've already added the pass will need to add the new one. Continue?"
    )) return;
    setBusy(true); setError(""); setFeedback("");
    try {
      const r = await adminApi.issueMemberCard(venueId, member.local_id, hasActive ? "reissue" : "issue");
      setFeedback(r.mode === "reissue" ? `Pass re-issued — ${r.superseded || 0} previous pass${(r.superseded || 0) === 1 ? "" : "es"} deactivated` : "Pass issued successfully");
      await reload();
    } catch (err) { setError(err.message); }
    setBusy(false);
  }

  async function setStatus(passId, status) {
    if (!window.confirm(`Set pass status to ${status}?`)) return;
    setError(""); setFeedback("");
    try {
      await adminApi.updatePassStatus(venueId, passId, status);
      await reload();
    } catch (err) { setError(err.message); }
  }

  const fullName = [member.firstname, member.surname].filter(Boolean).join(" ") || "(no name)";
  const initials = fullName === "(no name)"
    ? "?"
    : [member.firstname, member.surname].filter(Boolean).map((s) => s[0]?.toUpperCase()).join("").slice(0, 2);

  const litecardConfigured = preview?.enabled && preview?.templateName;
  const canIssue = isPortalManaged && litecardConfigured && (preview?.missingRequired?.length ?? 0) === 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-wide member-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 900 }}>
        {/* ── Header ─────────────────────────────────────── */}
        <div className="modal-header member-modal-header">
          <div className="member-avatar">{initials}</div>
          <div className="modal-header-text">
            <h2 style={{ fontSize: "1.15rem" }}>{fullName}</h2>
            <div className="member-header-pills">
              <code className="member-num">#{member.membernumber}</code>
              <span className="pill pill-muted">{providerLabel}</span>
              {member.suspensionflag === "1"
                ? <span className="pill pill-warn">Suspended</span>
                : <span className="pill pill-ok">Active</span>}
              {isPortalManaged
                ? <span className="pill pill-info">Portal-managed</span>
                : <span className="pill pill-muted">{providerLabel}-only</span>}
              {portalDetails?.id_document?.face_match_status && (
                <FaceMatchBadge status={portalDetails.id_document.face_match_status} size="sm" />
              )}
            </div>
          </div>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="modal-body member-modal-body">
          {feedback && <div className="toast toast-success" style={{ marginBottom: "1rem" }}>{feedback}</div>}
          {error    && <div className="toast toast-error"   style={{ marginBottom: "1rem" }}>{error}</div>}

          {/* Tab bar — Profile (default) + Logs (admin-only). Logs are
              hidden for provider-only members because we have nothing
              to show for them. */}
          {isPortalManaged && (
            <div style={{ display: "flex", gap: "0.5rem", borderBottom: "1px solid var(--border)", marginBottom: "1rem" }}>
              {[
                { key: "profile", label: "Profile" },
                { key: "logs",    label: "Logs" },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveTab(t.key)}
                  style={{
                    padding: "0.5rem 0.9rem",
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    color: activeTab === t.key ? "var(--accent)" : "var(--text-muted)",
                    borderBottom: activeTab === t.key ? "2px solid var(--accent)" : "2px solid transparent",
                    marginBottom: "-1px",
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {activeTab === "profile" && <>
          {/* ── Profile cards ──────────────────────────────── */}
          <section className="mm-section">
            <div className="mm-section-head"><span>Profile</span></div>
            <div className="info-grid">
              <InfoCard label="Email"        value={member.email} />
              <InfoCard label="Mobile"       value={member.mobile} />
              {member.dateofbirth && <InfoCard label="Date of birth" value={String(member.dateofbirth).slice(0, 10)} />}
              {member.product_name && <InfoCard label="Product" value={member.product_name} accent />}
              {member.local_date_expiry && <InfoCard label="Expires" value={String(member.local_date_expiry).slice(0, 10)} />}
              {member.synced_at && <InfoCard label="Last synced" value={new Date(member.synced_at).toLocaleString()} subtle />}
            </div>
          </section>

          {/* ── Identity verification (FOXID) ───────────────── */}
          {portalDetails?.id_document && (
            <div style={{ marginBottom: "1rem" }}>
              <IdentityVerificationCard
                doc={portalDetails.id_document}
                loadImage={(which) => adminApi.getMemberIdImage(venueId, member.local_id, which)}
              />
            </div>
          )}

          {/* ── Identity (additional portal fields) ─────────── */}
          {portalDetails && (
            <section className="mm-section">
              <div className="mm-section-head"><span>Identity</span></div>
              <div className="info-grid">
                {portalDetails.title       && <InfoCard label="Title"       value={portalDetails.title} />}
                {portalDetails.gender      && <InfoCard label="Gender"      value={portalDetails.gender} />}
                {portalDetails.telephone   && <InfoCard label="Telephone"   value={portalDetails.telephone} />}
                {portalDetails.occupation  && <InfoCard label="Occupation"  value={portalDetails.occupation} />}
                {portalDetails.card_number && <InfoCard label="Card number" value={portalDetails.card_number} />}
                {portalDetails.date_of_joining && (
                  <InfoCard label="Date of joining" value={String(portalDetails.date_of_joining).slice(0, 10)} />
                )}
              </div>
            </section>
          )}

          {/* ── Address ─────────────────────────────────────── */}
          {portalDetails && (portalDetails.address_line1 || portalDetails.postcode) && (
            <section className="mm-section">
              <div className="mm-section-head"><span>Address</span></div>
              <div className="info-grid">
                {portalDetails.address_line1 && <InfoCard label="Street"   value={portalDetails.address_line1} />}
                {portalDetails.address_line2 && <InfoCard label="Suburb"   value={portalDetails.address_line2} />}
                {portalDetails.state         && <InfoCard label="State"    value={portalDetails.state} />}
                {portalDetails.postcode      && <InfoCard label="Postcode" value={portalDetails.postcode} />}
              </div>
            </section>
          )}

          {/* ── Identification (IGT) ───────────────────────── */}
          {portalDetails && (portalDetails.identification_type || portalDetails.identification_number) && (
            <section className="mm-section">
              <div className="mm-section-head"><span>Identification</span></div>
              <div className="info-grid">
                {portalDetails.identification_type     && <InfoCard label="ID type"        value={portalDetails.identification_type} />}
                {portalDetails.identification_number   && <InfoCard label="Licence number" value={portalDetails.identification_number} />}
                {portalDetails.identification_expiry   && <InfoCard label="Expiry"         value={String(portalDetails.identification_expiry).slice(0, 10)} />}
                {portalDetails.identification_country_id && <InfoCard label="Country ID"   value={portalDetails.identification_country_id} />}
                {portalDetails.identification_state    && <InfoCard label="State"          value={portalDetails.identification_state} />}
              </div>
            </section>
          )}

          {/* ── Terms & Conditions acknowledgement ─────────── */}
          {portalDetails && portalDetails.terms_accepted_at && (
            <section className="mm-section">
              <div className="mm-section-head">
                <span>Terms &amp; Conditions</span>
                <span className="pill pill-ok" style={{ marginLeft: "0.5rem" }}>Accepted</span>
              </div>
              <div className="info-grid">
                <InfoCard label="Accepted at" value={new Date(portalDetails.terms_accepted_at).toLocaleString()} />
                {portalDetails.terms_version    && <InfoCard label="Version"    value={portalDetails.terms_version} />}
                {portalDetails.terms_signed_name && <InfoCard label="Signed by" value={portalDetails.terms_signed_name} />}
                {portalDetails.terms_accepted_ip && <InfoCard label="IP address" value={portalDetails.terms_accepted_ip} subtle />}
              </div>
              {portalDetails.terms_user_agent && (
                <div style={{ marginTop: "0.5rem", fontSize: "0.78rem", color: "var(--text-muted)", wordBreak: "break-all" }}>
                  <strong>User agent:</strong> {portalDetails.terms_user_agent}
                </div>
              )}
              {portalDetails.terms_signature && (
                <div style={{ marginTop: "0.75rem" }}>
                  <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginBottom: "0.35rem" }}>Signature</div>
                  <img
                    src={portalDetails.terms_signature}
                    alt="Member signature"
                    style={{ maxWidth: "100%", maxHeight: 140, background: "#fff", border: "1px solid var(--border)", borderRadius: 6, padding: 4 }}
                  />
                </div>
              )}
            </section>
          )}
          {portalDetails && !portalDetails.terms_accepted_at && (
            <section className="mm-section">
              <div className="mm-section-head">
                <span>Terms &amp; Conditions</span>
                <span className="pill pill-muted" style={{ marginLeft: "0.5rem" }}>No record</span>
              </div>
              <div style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
                Member predates T&amp;Cs acknowledgement, or this venue did not require it at the time.
              </div>
            </section>
          )}
          </>}

          {/* ── Logs tab ─────────────────────────────────────── */}
          {activeTab === "logs" && (
            <section className="mm-section">
              <div className="mm-section-head"><span>Activity</span></div>
              {logsLoading && <div className="mm-loading">Loading logs…</div>}
              {!logsLoading && memberLogs && (
                <>
                  <div className="mm-subhead">Join &amp; renewal events ({memberLogs.joinLogs?.length || 0})</div>
                  {memberLogs.joinLogs?.length ? (
                    <table className="data-table" style={{ fontSize: "0.82rem", marginBottom: "1rem" }}>
                      <thead>
                        <tr>
                          <th>When</th>
                          <th>Action</th>
                          <th>Status</th>
                          <th>Actor</th>
                          <th>IP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {memberLogs.joinLogs.map((l) => (
                          <tr key={l.id}>
                            <td>{new Date(l.created_at).toLocaleString()}</td>
                            <td><code>{l.action}</code></td>
                            <td>{l.status === "success"
                              ? <span className="pill pill-ok">success</span>
                              : <span className="pill pill-warn">{l.status}</span>}</td>
                            <td style={{ fontSize: "0.78rem" }}>{l.actor_name || l.actor_email || (l.action === "join" ? "Public signup" : "—")}</td>
                            <td style={{ fontSize: "0.78rem" }}>{l.ip_address || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <div style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "1rem" }}>No join logs.</div>}

                  <div className="mm-subhead">Venue API v1 requests ({memberLogs.apiLogs?.length || 0})</div>
                  {memberLogs.apiLogs?.length ? (
                    <table className="data-table" style={{ fontSize: "0.82rem" }}>
                      <thead>
                        <tr>
                          <th>When</th>
                          <th>Method</th>
                          <th>Path</th>
                          <th>Status</th>
                          <th>Latency</th>
                          <th>Key</th>
                        </tr>
                      </thead>
                      <tbody>
                        {memberLogs.apiLogs.map((l) => (
                          <tr key={l.id}>
                            <td>{new Date(l.created_at).toLocaleString()}</td>
                            <td><code>{l.method}</code></td>
                            <td style={{ fontFamily: "monospace", fontSize: "0.76rem", wordBreak: "break-all" }}>{l.path}</td>
                            <td>{l.status_code < 400
                              ? <span className="pill pill-ok">{l.status_code}</span>
                              : <span className="pill pill-warn">{l.status_code}</span>}</td>
                            <td style={{ fontSize: "0.78rem" }}>{l.duration_ms} ms</td>
                            <td style={{ fontSize: "0.78rem", fontFamily: "monospace" }}>{l.api_key_prefix || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <div style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>No API requests reference this membership number.</div>}
                </>
              )}
            </section>
          )}

          {activeTab === "profile" && <>
          {/* ── Digital pass section ───────────────────────── */}
          <section className="mm-section">
            <div className="mm-section-head">
              <span>Digital pass</span>
              {litecardConfigured && (
                <span className="mm-section-meta">
                  Template: <strong>{preview.templateName}</strong> · {preview.fields.filter((f) => f.mappedFrom).length} of {preview.fields.length} fields mapped
                </span>
              )}
            </div>

            {/* Status / blockers */}
            {!isPortalManaged && (
              <div className="mm-banner mm-banner-warn">
                Member exists in {providerLabel} only — passes can only be issued for portal-managed members.
                Run a single-member import or have them sign up via the portal first.
              </div>
            )}
            {isPortalManaged && previewError && (
              <div className="mm-banner mm-banner-warn">{previewError}</div>
            )}
            {isPortalManaged && preview && !litecardConfigured && (
              <div className="mm-banner mm-banner-warn">
                Litecard isn't enabled or no template is linked for this venue. Set it up in <em>Digital Card → Credentials/Template</em>.
              </div>
            )}
            {isPortalManaged && preview?.missingRequired?.length > 0 && (
              <div className="mm-banner mm-banner-warn">
                <strong>Missing required data:</strong>{" "}
                {preview.missingRequired.map((f) => f.label).join(", ")}.{" "}
                {preview.missingRequired.some((f) => !f.mappedFrom)
                  ? "Some required template fields aren't mapped — fix in Digital Card → Template & Mapping."
                  : "Source data is empty for this member."}
              </div>
            )}

            {/* Issue / re-issue button + preview toggle */}
            {isPortalManaged && (
              <div className="mm-issue-actions">
                <button
                  className="btn btn-primary"
                  disabled={busy || !canIssue}
                  onClick={issue}
                  title={!canIssue ? "Resolve the blockers above first" : ""}
                >
                  {busy
                    ? busyLabel
                    : <>
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: -2 }}>
                          <rect x="2" y="6" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>
                        </svg>
                        {buttonLabel}
                      </>}
                </button>
                {preview && (
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowPreview((v) => !v)}>
                    {showPreview ? "Hide" : "Preview"} pass data
                  </button>
                )}
              </div>
            )}

            {/* Pass data preview */}
            {showPreview && preview && (
              <div className="mm-preview">
                <table className="data-table mm-preview-table">
                  <thead>
                    <tr><th>Template field</th><th>Mapped from</th><th>Value for this member</th></tr>
                  </thead>
                  <tbody>
                    {preview.fields.map((f) => {
                      const empty = f.value == null || f.value === "";
                      return (
                        <tr key={f.key}>
                          <td>
                            <strong>{f.label}</strong>
                            {f.required && <span style={{ color: "var(--danger)", marginLeft: 4 }}>*</span>}
                            <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", fontFamily: "monospace" }}>{f.key}</div>
                          </td>
                          <td style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
                            {f.mappedFrom || <em>(unmapped)</em>}
                          </td>
                          <td style={{ fontSize: "0.85rem" }}>
                            {empty
                              ? <span style={{ color: f.required ? "var(--danger)" : "var(--text-muted)" }}>{f.required ? "MISSING" : "—"}</span>
                              : <span>{String(f.value)}</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {preview.barcode?.fieldMap && (
                  <div className="mm-preview-foot">
                    Barcode encodes <strong>{preview.barcode.fieldMap}</strong>
                    {preview.memberData?.[preview.barcode.fieldMap] && <> = <code>{preview.memberData[preview.barcode.fieldMap]}</code></>}
                  </div>
                )}
              </div>
            )}

            {previewLoading && !preview && (
              <div className="mm-loading">Loading pass data…</div>
            )}

            {/* Existing passes list */}
            {isPortalManaged && (
              <div style={{ marginTop: "1rem" }}>
                <div className="mm-subhead">Issued passes {passes.length > 0 ? `(${passes.length})` : ""}</div>
                {loading ? (
                  <div className="mm-loading">Loading passes…</div>
                ) : passes.length ? (
                  <div className="mm-pass-list">
                    {passes.map((p) => (
                      <div key={p.id} className={`mm-pass ${p.status === "ACTIVE" ? "is-active" : "is-inactive"}`}>
                        <div className="mm-pass-status">
                          <span className={`pill ${p.status === "ACTIVE" ? "pill-ok" : "pill-muted"}`}>{p.status}</span>
                        </div>
                        <div className="mm-pass-meta">
                          <div className="mm-pass-date">Issued {new Date(p.created_at).toLocaleString()}</div>
                          <div className="mm-pass-links">
                            {p.apple_link  ? <a href={p.apple_link}  target="_blank" rel="noreferrer" className="wallet-pill wallet-apple">  Apple Wallet</a>  : null}
                            {p.google_link ? <a href={p.google_link} target="_blank" rel="noreferrer" className="wallet-pill wallet-google"> Google Wallet</a> : null}
                            {!p.apple_link && !p.google_link && <span style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>(no wallet links yet)</span>}
                          </div>
                        </div>
                        <div className="mm-pass-actions">
                          {p.status === "ACTIVE"
                            ? <button className="btn btn-outline btn-sm" onClick={() => setStatus(p.id, "DEACTIVATED")}>Deactivate</button>
                            : <button className="btn btn-outline btn-sm" onClick={() => setStatus(p.id, "ACTIVE")}>Reactivate</button>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state mm-empty">
                    <p style={{ margin: 0, color: "var(--text-muted)" }}>No passes issued for this member yet — click <strong>Issue pass</strong> above.</p>
                  </div>
                )}
              </div>
            )}
          </section>
          </>}
        </div>
      </div>
    </div>
  );
}

function InfoCard({ label, value, accent, subtle }) {
  return (
    <div className={`info-card ${accent ? "is-accent" : ""} ${subtle ? "is-subtle" : ""}`}>
      <div className="info-card-label">{label}</div>
      <div className="info-card-value">{value || "—"}</div>
    </div>
  );
}

/* Renders a row per sync type with last-sync time, row count, per-row
 * "Sync now" button, and a single "Sync everything" button at the top.
 * Expects `syncs` in the new shape:
 *   [{ key, last_sync_at, status, error, row_count, duration_ms }, ...]
 */
/* S7000 sync types — each backed by its own Oracle SQL view. */
const S7000_SYNC_ROWS = [
  { key: "s7000_members",  label: "Members",       sublabel: "V_PUB_MEMBERS + V_PUB_MEM_OTHERDETAILS", part: "members"  },
  { key: "s7000_analysis", label: "Analysis tags", sublabel: "V_PUB_MEMBER_ANALYSIS",                   part: "analysis" },
  { key: "s7000_cards",    label: "Cards",         sublabel: "V_PUB_CARDS",                             part: "cards"    },
  { key: "occupations",    label: "Occupations",   sublabel: "V_PUB_OCCUPATION_LIST",                   part: "occupations" },
];

/* IGT reference-data sync types — each fetched from its own MeRS
 * endpoint. IGT has no bulk member list, so members are NOT in this
 * table; they're imported on-demand (see the Import panel below). */
const IGT_SYNC_ROWS = [
  { key: "titles",               label: "Titles",              sublabel: "GET /memberships/titles",                   part: "igt" },
  { key: "genders",              label: "Genders",             sublabel: "GET /memberships/genders",                  part: "igt" },
  { key: "countries",            label: "Countries",           sublabel: "GET /memberships/countries",                part: "igt" },
  { key: "attraction_types",     label: "Attraction types",    sublabel: "GET /memberships/attraction_types",         part: "igt" },
  { key: "identification_types", label: "Identification types",sublabel: "GET /memberships/identification_types",     part: "igt" },
  { key: "membership_types",     label: "Membership types",    sublabel: "GET /memberships/types",                    part: "igt" },
  { key: "membership_terms",     label: "Membership terms",    sublabel: "GET /memberships/terms",                    part: "igt" },
  { key: "mandatory_fields",     label: "Mandatory fields",    sublabel: "GET /sites/mandatory_fields",               part: "igt" },
  { key: "phone_types",          label: "Phone types",         sublabel: "GET /memberships/phone_types",              part: "igt" },
  { key: "email_types",          label: "Email types",         sublabel: "GET /memberships/email_types",              part: "igt" },
  { key: "address_types",        label: "Address types",       sublabel: "GET /memberships/address_types",            part: "igt" },
  { key: "marketing_options",    label: "Marketing options",   sublabel: "GET /marketing/options",                    part: "igt" },
  { key: "marketing_reasons",    label: "Marketing reasons",   sublabel: "GET /marketing/reasons",                    part: "igt" },
];

function SyncPanel({ venueId, providerType, syncs, reload, onSyncAll, syncingAll }) {
  const [busy, setBusy] = useState({});
  const [error, setError] = useState("");
  const [igtMemberCount, setIgtMemberCount] = useState(null);

  const rowsDef = (providerType === "igt") ? IGT_SYNC_ROWS : S7000_SYNC_ROWS;
  const byKey = Object.fromEntries(syncs.map((s) => [s.key, s]));

  // For IGT venues, show a count of imported members alongside (separate
  // from the ref-data rows above — there's no bulk-sync for members).
  useEffect(() => {
    if (providerType !== "igt") { setIgtMemberCount(null); return; }
    adminApi.cachedMembers(venueId, { limit: 1, offset: 0 })
      .then((r) => setIgtMemberCount(r?.total ?? 0))
      .catch(() => {});
  }, [providerType, venueId, syncs]);

  async function syncOne(k) {
    setBusy((b) => ({ ...b, [k.key]: true })); setError("");
    try {
      await adminApi.syncPart(venueId, k.part);
      setTimeout(reload, 1200);
    } catch (err) { setError(err.message); }
    setBusy((b) => ({ ...b, [k.key]: false }));
  }

  const latestAt = syncs
    .map((s) => s.last_sync_at ? new Date(s.last_sync_at).getTime() : 0)
    .reduce((a, b) => Math.max(a, b), 0);

  return (
    <div className="sync-status-panel">
      <div className="sync-status-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <span className="sync-status-title" style={{ fontWeight: 600 }}>
            {providerType === "igt" ? "IGT reference data" : "S7000 cached data"}
          </span>
          <div style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
            {latestAt
              ? <>Last sync: {new Date(latestAt).toLocaleString()} <span style={{ opacity: 0.7 }}>({timeAgo(latestAt)})</span></>
              : "Not yet synced"}
          </div>
        </div>
        <button type="button" className="btn btn-primary btn-sm" onClick={onSyncAll} disabled={syncingAll}>
          {syncingAll ? "Syncing…" : "Sync everything"}
        </button>
      </div>

      {error && <div className="toast toast-error" style={{ margin: "0.5rem 0" }}>{error}</div>}

      <div className="sync-status-rows" style={{ marginTop: "0.5rem" }}>
        {rowsDef.map((k) => {
          const s = byKey[k.key];
          const status = s?.status || "never";
          const color = status === "ok"      ? "var(--success)"
                      : status === "error"   ? "var(--danger)"
                      : status === "running" ? "var(--warning)"
                      : "var(--text-muted)";
          return (
            <div key={k.key} className="sync-status-row" style={{ display: "grid", gridTemplateColumns: "12px 1fr auto auto auto", alignItems: "center", gap: "0.75rem", padding: "0.5rem 0.25rem", borderBottom: "1px solid var(--border)" }}>
              <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 999, background: color }} />
              <div>
                <div style={{ fontWeight: 500, fontSize: "0.9rem" }}>{k.label}</div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                  <code style={{ background: "transparent", padding: 0 }}>{k.sublabel}</code>
                  {" · "}
                  {s?.last_sync_at
                    ? <>last sync {new Date(s.last_sync_at).toLocaleString()} <span style={{ opacity: 0.75 }}>({timeAgo(s.last_sync_at)})</span></>
                    : "never synced"}
                  {s?.duration_ms ? ` · ${Math.round(s.duration_ms / 1000)}s` : ""}
                </div>
                {s?.error && <div style={{ fontSize: "0.75rem", color: "var(--danger)", marginTop: "0.15rem" }}>⚠ {s.error}</div>}
              </div>
              <span style={{ fontSize: "0.85rem", color: "var(--text-muted)", fontVariantNumeric: "tabular-nums" }}>
                {s?.row_count != null ? `${s.row_count.toLocaleString()} rows` : "—"}
              </span>
              <span style={{ fontSize: "0.75rem", color, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>{status}</span>
              <button type="button" className="btn btn-outline btn-sm" disabled={!!busy[k.key]} onClick={() => syncOne(k)}>
                {busy[k.key] ? "Starting…" : "Sync now"}
              </button>
            </div>
          );
        })}
      </div>

      {providerType === "igt" && (
        <div style={{ marginTop: "0.75rem", padding: "0.6rem 0.85rem", background: "var(--bg-elevated)", borderRadius: 8, fontSize: "0.85rem" }}>
          <strong>Members:</strong>{" "}
          {igtMemberCount == null
            ? <span style={{ color: "var(--text-muted)" }}>—</span>
            : <>{igtMemberCount.toLocaleString()} cached</>}
          <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>
            IGT's MeRS has no bulk-list endpoint — members are imported on-demand via the form below, or auto-imported the first time they're opened in the Venue Portal.
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminVenueDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [venue, setVenue] = useState(null);
  const [members, setMembers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [clients, setClients] = useState([]);
  // Persisted in URL so refresh keeps the operator on the same tab/section.
  // ?tab=members&section=email → admin lands here after F5 / share link.
  const [tab, setTab]                     = useUrlState("tab", "settings");
  const [activeSection, setActiveSection] = useUrlState("section", "general");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null); // null | 'ok' | 'fail'
  const [testResults, setTestResults] = useState(null); // { provider, checks[], latency }

  const [venueForm, setVenueForm] = useState({ name: "", slug: "", logo_url: "", primary_color: "#3b82f6", accent_color: "#1e293b", background_color: "#ffffff", text_color: "#1e293b", button_text_color: "#ffffff", button_radius: 8, font_family: "Inter", welcome_title: "", welcome_subtitle: "", choice_title: "", choice_subtitle: "", signup_text_styles: {}, signup_layout: {}, signup_benefits: null, cover_image_url: "", provider_type: "s7000", duplicate_check_enabled: false, terms_required: false, terms_source: "inline", terms_display_mode: "new_tab", terms_title: "", terms_body: "", terms_url: "", terms_version: "", agm_enabled: false, agm_copy: "", agm_version: "", default_marketing_reason_id: "", marketing_reason_id_new: "", marketing_reason_id_renewal: "", hidden_menus: [] });
  // Comms-prefs state — channel list + reason list. Both lazy-loaded
  // when the Comms subtab is first activated to avoid an extra fetch
  // for venues whose admin never opens that section.
  const [commsOptions, setCommsOptions] = useState([]);
  const [commsReasons, setCommsReasons] = useState([]);
  const [newCommsOption, setNewCommsOption] = useState("");
  const [providerForm, setProviderForm] = useState({
    s7000_base_url: "", s7000_reg_code: "", s7000_user_id: "", s7000_password: "",
    sandbox_s7000_base_url: "", sandbox_s7000_reg_code: "", sandbox_s7000_user_id: "", sandbox_s7000_password: "",
  });
  const [igtForm, setIgtForm] = useState({
    igt_srs_url: "", igt_mers_url: "", igt_username: "", igt_password: "",
    igt_external_id: "4434", igt_mac_key: "", igt_site_id: 1, igt_membership_type_id: 2, igt_term_id: 1,
    igt_membership_class_id: "", igt_payment_method_id: "",
    igt_validation_type: "HMAC SHA512", igt_attendant_code: "",
    sandbox_igt_srs_url: "", sandbox_igt_mers_url: "", sandbox_igt_username: "", sandbox_igt_password: "",
    sandbox_igt_external_id: "", sandbox_igt_mac_key: "",
    sandbox_igt_site_id: "", sandbox_igt_membership_type_id: "", sandbox_igt_term_id: "",
    sandbox_igt_validation_type: "", sandbox_igt_attendant_code: "",
  });
  const [swiftposForm, setSwiftposForm] = useState({
    swiftpos_base_url: "", swiftpos_integrator_name: "", swiftpos_integrator_key: "",
    swiftpos_clerk_id: "0", swiftpos_client_id: "", swiftpos_customer_id: "",
    swiftpos_classification_id: "", swiftpos_renewal_months: 12,
    swiftpos_exclusion_mode: "off", swiftpos_exclusion_classification_ids: "",
    sandbox_swiftpos_base_url: "", sandbox_swiftpos_integrator_name: "", sandbox_swiftpos_integrator_key: "",
    sandbox_swiftpos_clerk_id: "", sandbox_swiftpos_client_id: "", sandbox_swiftpos_customer_id: "",
    sandbox_swiftpos_classification_id: "", sandbox_swiftpos_renewal_months: "",
    sandbox_swiftpos_exclusion_mode: "off", sandbox_swiftpos_exclusion_classification_ids: "",
  });
  const [circlescanForm, setCirclescanForm] = useState({
    circlescan_base_url: "", circlescan_api_key: "",
    circlescan_default_status: "", circlescan_default_type: "", circlescan_default_term_id: "", circlescan_renewal_months: 12,
    sandbox_circlescan_base_url: "", sandbox_circlescan_api_key: "",
    sandbox_circlescan_default_status: "", sandbox_circlescan_default_type: "", sandbox_circlescan_default_term_id: "", sandbox_circlescan_renewal_months: "",
  });
  const [providerEnv, setProviderEnv] = useState("live"); // live | sandbox sub-tab
  // Helper: returns the live or sandbox column name for a given base
  // field, based on the active sub-tab. Keeps the JSX terse.
  const pKey = (base) => providerEnv === "sandbox" ? `sandbox_${base}` : base;
  // Human label for a CircleScan term row. NB: `Term` is an integer whose unit
  // CircleScan doesn't specify (months vs years), so show it unqualified rather
  // than inventing a unit; the rate gives the admin the real context.
  const circlescanTermLabel = (t) =>
    `Term ${t.term != null ? t.term : "?"}${t.rate ? ` · ${t.rate}` : ""}${t.is_default ? " (default)" : ""} (#${t.term_id})`;
  // Selectable status / type / term options. Prefer CircleScan's configured
  // reference data; when a tenant has none (its Statuses/Types/Terms endpoints
  // legitimately return []), fall back to the distinct values real members are
  // already using so the admin still gets a list instead of a blank box.
  // Labels lead with the human Description — CircleScan status codes are bare
  // numbers ("0".."3"), so "Financial (1)" reads far better than "1 — Financial".
  // The stored VALUE is always the Code, which is what the API expects.
  const csStatusOptions = () =>
    circlescanRefData.statuses.length
      ? circlescanRefData.statuses.map((s) => ({ value: s.code, label: `${s.description || s.code}${s.description ? ` (${s.code})` : ""}${s.is_default ? " — default" : ""}` }))
      : (circlescanRefData.observed?.statuses || []).map((c) => ({ value: c, label: `${c} (in use)` }));
  const csTypeOptions = () =>
    circlescanRefData.types.length
      ? circlescanRefData.types.map((t) => ({ value: t.code, label: `${t.description || t.code}${t.description ? ` (${t.code})` : ""}${t.is_default ? " — default" : ""}` }))
      : (circlescanRefData.observed?.types || []).map((c) => ({ value: c, label: `${c} (in use)` }));
  const csTermOptions = (typeCode) =>
    circlescanRefData.terms.length
      ? circlescanTermsForType(typeCode).map((t) => ({ value: String(t.term_id), label: circlescanTermLabel(t) }))
      : (circlescanRefData.observed?.term_ids || []).map((n) => ({ value: String(n), label: `Term #${n} (in use)` }));

  // Flags saved defaults that don't exist in CircleScan's synced reference data.
  // Without this an invalid code sits in the config until a real signup fails
  // with "Such member status does not exist" — a 400 at the worst moment.
  const csInvalidDefaults = () => {
    if (!circlescanRefData.statuses.length && !circlescanRefData.types.length && !circlescanRefData.terms.length) return [];
    const bad = [];
    const status = circlescanForm[pKey("circlescan_default_status")];
    const type   = circlescanForm[pKey("circlescan_default_type")];
    const term   = circlescanForm[pKey("circlescan_default_term_id")];
    if (status && circlescanRefData.statuses.length && !circlescanRefData.statuses.some((s) => s.code === status))
      bad.push(`status "${status}"`);
    if (type && circlescanRefData.types.length && !circlescanRefData.types.some((t) => t.code === type))
      bad.push(`type "${type}"`);
    if (term !== "" && term != null && circlescanRefData.terms.length && !circlescanRefData.terms.some((t) => String(t.term_id) === String(term)))
      bad.push(`term #${term}`);
    return bad;
  };

  // One-line explanation under "Membership Defaults" so a synced-but-empty
  // provider is never mistaken for "you haven't synced yet".
  const csRefDataNote = () => {
    const s = circlescanRefData.sync;
    const haveRef = circlescanRefData.statuses.length || circlescanRefData.types.length || circlescanRefData.terms.length;
    const haveObs = (circlescanRefData.observed?.statuses?.length || 0) + (circlescanRefData.observed?.types?.length || 0) + (circlescanRefData.observed?.term_ids?.length || 0);
    if (s?.error) return { tone: "warn", text: `Last reference-data sync failed: ${s.error}` };
    if (haveRef) return null;
    if (!s?.has_run) return { tone: "info", text: 'No reference data loaded yet — click "Sync from CircleScan" to fetch statuses, types and terms.' };
    const when = s.last_synced_at ? new Date(s.last_synced_at).toLocaleString() : "recently";
    return {
      tone: "warn",
      text: `Synced ${when}, but CircleScan returned no statuses, types or terms for this venue — they haven't been configured in CircleScan yet.` +
            (haveObs ? " Showing the values existing members use; you can also type a code directly." : " Type the codes directly, or configure them in CircleScan and sync again."),
    };
  };

  // Terms belonging to the given member-type code (terms carry a numeric
  // type_id). No type selected → all terms; unknown code → all terms.
  const circlescanTermsForType = (typeCode) => {
    const terms = circlescanRefData.terms || [];
    if (!typeCode) return terms;
    const type = (circlescanRefData.types || []).find((t) => t.code === typeCode);
    if (!type) return terms;
    return terms.filter((t) => t.type_id == null || Number(t.type_id) === Number(type.type_id));
  };
  const [stripeForm, setStripeForm] = useState({
    stripe_secret_key: "", stripe_publishable_key: "", stripe_webhook_secret: "",
    sandbox_stripe_secret_key: "", sandbox_stripe_publishable_key: "", sandbox_stripe_webhook_secret: "",
  });
  const [stripeEnv, setStripeEnv] = useState("live"); // live | sandbox sub-tab
  const [clientForm, setClientForm] = useState({ email: "", password: "", name: "" });
  const [showInvite, setShowInvite] = useState(false);
  const [showResetPw, setShowResetPw] = useState(false);
  const [resetPwTarget, setResetPwTarget] = useState(null);
  const [resetPwVal, setResetPwVal] = useState("");
  const [savingResetPw, setSavingResetPw] = useState(false);

  // Products state
  const [products, setProducts]           = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editProduct, setEditProduct]     = useState(null);
  const [productForm, setProductForm]     = useState({ name: "", description: "", price: "", currency: "aud", duration_months: 12, sync_to_stripe: false, analysis_codes: [], igt_membership_type_id: "", igt_term_id: "", swiftpos_classification_id: "", circlescan_type: "", circlescan_term_id: "" });
  const [savingProduct, setSavingProduct] = useState(false);

  // IGT Import Wizard state
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [importStep, setImportStep]       = useState(0); // 0=loading, 1=select, 2=pricing, 3=creating, 4=done
  const [igtTypes, setIgtTypes]           = useState([]);
  const [igtTerms, setIgtTerms]           = useState([]);
  const [importCandidates, setImportCandidates] = useState([]); // { key, typeId, typeName, termId, termName, duration_months, selected, price, name }
  const [importError, setImportError]     = useState("");
  const [importResults, setImportResults] = useState([]); // { name, status: 'ok'|'fail', detail }

  // Sync status
  const [syncStatus, setSyncStatus] = useState([]);
  const [syncing, setSyncing] = useState(false);
  // Import single member
  const [importMemberInput, setImportMemberInput] = useState("");
  const [importMemberBusy, setImportMemberBusy]   = useState(false);
  const [importMemberMsg, setImportMemberMsg]     = useState(null); // { ok, text }
  // Connection health
  const [connectionHealth, setConnectionHealth] = useState(null);
  const [healthLoading, setHealthLoading]       = useState(false);
  // Analysis codes (for product modal)
  const [analysisTypes, setAnalysisTypes] = useState([]);
  const [analysisRules, setAnalysisRules] = useState([]);
  // Cached membership types/terms for IGT dropdowns
  const [cachedMemberTypes, setCachedMemberTypes] = useState([]);
  const [cachedMemberTerms, setCachedMemberTerms] = useState([]);
  const [swiftposClassifications, setSwiftposClassifications] = useState([]);
  const [swiftposSyncing, setSwiftposSyncing] = useState(false);
  // Cached CircleScan reference data (statuses/types/terms) for the default +
  // per-product dropdowns.
  const [circlescanRefData, setCirclescanRefData] = useState({ statuses: [], types: [], terms: [], sync: null, observed: { statuses: [], types: [], term_ids: [] } });
  const [circlescanSyncing, setCirclescanSyncing] = useState(false);
  // CircleScan reference-data management (add / edit statuses, types, terms).
  const [csRefModal, setCsRefModal] = useState(null);   // { kind, item|null }
  const [csRefForm, setCsRefForm]   = useState({});
  const [csRefSaving, setCsRefSaving] = useState(false);
  const [csRefError, setCsRefError] = useState("");   // shown inside the modal

  useEffect(() => { loadVenue(); }, [id]);

  // Lazy-load IGT membership types + terms the first time the product
  // modal opens on an IGT venue. Used to populate the
  // "IGT Membership Type" / "IGT Term" dropdowns. The Import Wizard
  // populates the same state when it opens; this just makes sure the
  // regular product form can also map products without going through
  // the wizard.
  useEffect(() => {
    if (!showProductModal) return;
    if (venueForm.provider_type !== "igt") return;
    if (igtTypes.length > 0 || igtTerms.length > 0) return;
    adminApi.getIgtMembershipTypes(id)
      .then((d) => { setIgtTypes(d.types || []); setIgtTerms(d.terms || []); })
      .catch(() => {});
    // eslint-disable-next-line
  }, [showProductModal, venueForm.provider_type]);
  // Load SwiftPOS classifications when the product modal opens (for the
  // per-product classification dropdown), mirroring the IGT lazy-load above.
  useEffect(() => {
    if (!showProductModal || venueForm.provider_type !== "swiftpos") return;
    if (swiftposClassifications.length > 0) return;
    loadSwiftposClassifications();
    // eslint-disable-next-line
  }, [showProductModal, venueForm.provider_type]);
  // Lazy-load CircleScan ref data when the product modal opens (per-product
  // type + term dropdowns), mirroring the SwiftPOS/IGT lazy-loads above.
  useEffect(() => {
    if (!showProductModal || venueForm.provider_type !== "circlescan") return;
    if (circlescanRefData.types.length > 0 || circlescanRefData.terms.length > 0) return;
    loadCirclescanRefData();
    // eslint-disable-next-line
  }, [showProductModal, venueForm.provider_type]);
  useEffect(() => { if (activeSection === "provider") { loadSyncStatus(); loadCachedMembershipData(); loadSwiftposClassifications(); loadCirclescanRefData(); } }, [activeSection]);
  useEffect(() => { if (activeSection === "general") loadConnectionHealth(); /* eslint-disable-next-line */ }, [activeSection]);
  // Lazy-load comms-prefs lists when the tab is opened. Re-run on
  // provider switch so a venue moved from S7000 → IGT (or vice versa)
  // picks up the right reason list / channel source.
  useEffect(() => {
    if (activeSection !== "comms") return;
    adminApi.getVenueMarketingOptions(id).then(setCommsOptions).catch(() => setCommsOptions([]));
    adminApi.getVenueMarketingReasons(id).then(setCommsReasons).catch(() => setCommsReasons([]));
    // eslint-disable-next-line
  }, [activeSection, venueForm.provider_type]);

  async function loadVenue() {
    try {
      const data = await adminApi.getVenue(id);
      setVenue(data);
      setVenueForm({ name: data.name || "", slug: data.slug || "", logo_url: data.logo_url || "", primary_color: data.primary_color || "#3b82f6", accent_color: data.accent_color || "#1e293b", background_color: data.background_color || "#ffffff", text_color: data.text_color || "#1e293b", button_text_color: data.button_text_color || "#ffffff", button_radius: data.button_radius ?? 8, font_family: data.font_family || "Inter", welcome_title: data.welcome_title || "", welcome_subtitle: data.welcome_subtitle || "", choice_title: data.choice_title || "", choice_subtitle: data.choice_subtitle || "", signup_text_styles: (data.signup_text_styles && typeof data.signup_text_styles === "object") ? data.signup_text_styles : {}, signup_layout: (data.signup_layout && typeof data.signup_layout === "object" && !Array.isArray(data.signup_layout)) ? data.signup_layout : {}, signup_benefits: Array.isArray(data.signup_benefits) ? data.signup_benefits : null, cover_image_url: data.cover_image_url || "", provider_type: data.provider_type || "s7000", duplicate_check_enabled: !!data.duplicate_check_enabled, signup_enabled: data.signup_enabled !== false, renewal_enabled: data.renewal_enabled !== false, digital_passes_enabled: data.digital_passes_enabled !== false, gender_field_enabled: data.gender_field_enabled !== false, ga4_measurement_id: data.ga4_measurement_id || "", meta_pixel_id: data.meta_pixel_id || "", terms_required: !!data.terms_required, terms_require_signature: data.terms_require_signature !== false, terms_source: data.terms_source || "inline", terms_display_mode: data.terms_display_mode || "new_tab", terms_title: data.terms_title || "", terms_body: data.terms_body || "", terms_url: data.terms_url || "", terms_version: data.terms_version || "", agm_enabled: !!data.agm_enabled, comms_step_enabled: data.comms_step_enabled !== false, agm_copy: data.agm_copy || "", agm_version: data.agm_version || "", default_marketing_reason_id: data.default_marketing_reason_id ?? "", marketing_reason_id_new: data.marketing_reason_id_new ?? "", marketing_reason_id_renewal: data.marketing_reason_id_renewal ?? "", hidden_menus: Array.isArray(data.hidden_menus) ? data.hidden_menus : [] });
      if (data.providerConfig) {
        setProviderForm({
          s7000_base_url: data.providerConfig.s7000_base_url || "",
          s7000_reg_code: data.providerConfig.s7000_reg_code || "",
          s7000_user_id:  data.providerConfig.s7000_user_id  || "",
          s7000_password: data.providerConfig.s7000_password || "",
          sandbox_s7000_base_url: data.providerConfig.sandbox_s7000_base_url || "",
          sandbox_s7000_reg_code: data.providerConfig.sandbox_s7000_reg_code || "",
          sandbox_s7000_user_id:  data.providerConfig.sandbox_s7000_user_id  || "",
          sandbox_s7000_password: data.providerConfig.sandbox_s7000_password || "",
        });
      }
      if (data.igtConfig) {
        setIgtForm((prev) => ({
          ...prev,
          igt_srs_url: data.igtConfig.igt_srs_url || "",
          igt_mers_url: data.igtConfig.igt_mers_url || "",
          igt_username: data.igtConfig.igt_username || "",
          igt_password: data.igtConfig.igt_password || "",
          igt_external_id: data.igtConfig.igt_external_id || "4434",
          igt_mac_key: data.igtConfig.igt_mac_key || "",
          igt_validation_type: data.igtConfig.igt_validation_type || "HMAC SHA512",
          igt_attendant_code: data.igtConfig.igt_attendant_code || "",
          igt_site_id: data.igtConfig.igt_site_id ?? 1,
          sandbox_igt_srs_url:     data.igtConfig.sandbox_igt_srs_url     || "",
          sandbox_igt_mers_url:    data.igtConfig.sandbox_igt_mers_url    || "",
          sandbox_igt_username:    data.igtConfig.sandbox_igt_username    || "",
          sandbox_igt_password:    data.igtConfig.sandbox_igt_password    || "",
          sandbox_igt_external_id: data.igtConfig.sandbox_igt_external_id || "",
          sandbox_igt_mac_key:     data.igtConfig.sandbox_igt_mac_key     || "",
          sandbox_igt_validation_type: data.igtConfig.sandbox_igt_validation_type || "",
          sandbox_igt_attendant_code:  data.igtConfig.sandbox_igt_attendant_code  || "",
          sandbox_igt_site_id:           data.igtConfig.sandbox_igt_site_id           ?? "",
          sandbox_igt_membership_type_id:data.igtConfig.sandbox_igt_membership_type_id ?? "",
          sandbox_igt_term_id:           data.igtConfig.sandbox_igt_term_id           ?? "",
          igt_membership_type_id: data.igtConfig.igt_membership_type_id || 2,
          igt_term_id: data.igtConfig.igt_term_id || 1,
          igt_membership_class_id: data.igtConfig.igt_membership_class_id ?? "",
          igt_payment_method_id: data.igtConfig.igt_payment_method_id ?? "",
        }));
      }
      if (data.swiftposConfig) {
        setSwiftposForm((prev) => ({
          ...prev,
          swiftpos_base_url:          data.swiftposConfig.swiftpos_base_url || "",
          swiftpos_integrator_name:   data.swiftposConfig.swiftpos_integrator_name || "",
          swiftpos_integrator_key:    data.swiftposConfig.swiftpos_integrator_key || "",
          swiftpos_clerk_id:          data.swiftposConfig.swiftpos_clerk_id ?? "0",
          swiftpos_client_id:         data.swiftposConfig.swiftpos_client_id || "",
          swiftpos_customer_id:       data.swiftposConfig.swiftpos_customer_id || "",
          swiftpos_classification_id: data.swiftposConfig.swiftpos_classification_id ?? "",
          swiftpos_renewal_months:    data.swiftposConfig.swiftpos_renewal_months ?? 12,
          swiftpos_exclusion_mode:               data.swiftposConfig.swiftpos_exclusion_mode || "off",
          swiftpos_exclusion_classification_ids: data.swiftposConfig.swiftpos_exclusion_classification_ids || "",
          sandbox_swiftpos_exclusion_mode:               data.swiftposConfig.sandbox_swiftpos_exclusion_mode || "off",
          sandbox_swiftpos_exclusion_classification_ids: data.swiftposConfig.sandbox_swiftpos_exclusion_classification_ids || "",
          sandbox_swiftpos_base_url:          data.swiftposConfig.sandbox_swiftpos_base_url || "",
          sandbox_swiftpos_integrator_name:   data.swiftposConfig.sandbox_swiftpos_integrator_name || "",
          sandbox_swiftpos_integrator_key:    data.swiftposConfig.sandbox_swiftpos_integrator_key || "",
          sandbox_swiftpos_clerk_id:          data.swiftposConfig.sandbox_swiftpos_clerk_id ?? "",
          sandbox_swiftpos_client_id:         data.swiftposConfig.sandbox_swiftpos_client_id || "",
          sandbox_swiftpos_customer_id:       data.swiftposConfig.sandbox_swiftpos_customer_id || "",
          sandbox_swiftpos_classification_id: data.swiftposConfig.sandbox_swiftpos_classification_id ?? "",
          sandbox_swiftpos_renewal_months:    data.swiftposConfig.sandbox_swiftpos_renewal_months ?? "",
        }));
      }
      if (data.circlescanConfig) {
        setCirclescanForm((prev) => ({
          ...prev,
          circlescan_base_url:        data.circlescanConfig.circlescan_base_url || "",
          circlescan_api_key:         data.circlescanConfig.circlescan_api_key || "",
          circlescan_default_status:  data.circlescanConfig.circlescan_default_status || "",
          circlescan_default_type:    data.circlescanConfig.circlescan_default_type || "",
          circlescan_default_term_id: data.circlescanConfig.circlescan_default_term_id ?? "",
          circlescan_renewal_months:  data.circlescanConfig.circlescan_renewal_months ?? 12,
          sandbox_circlescan_base_url:        data.circlescanConfig.sandbox_circlescan_base_url || "",
          sandbox_circlescan_api_key:         data.circlescanConfig.sandbox_circlescan_api_key || "",
          sandbox_circlescan_default_status:  data.circlescanConfig.sandbox_circlescan_default_status || "",
          sandbox_circlescan_default_type:    data.circlescanConfig.sandbox_circlescan_default_type || "",
          sandbox_circlescan_default_term_id: data.circlescanConfig.sandbox_circlescan_default_term_id ?? "",
          sandbox_circlescan_renewal_months:  data.circlescanConfig.sandbox_circlescan_renewal_months ?? "",
        }));
      }
      if (data.stripeConfig) {
        setStripeForm({
          stripe_secret_key:              data.stripeConfig.stripe_secret_key || "",
          stripe_publishable_key:         data.stripeConfig.stripe_publishable_key || "",
          stripe_webhook_secret:          data.stripeConfig.stripe_webhook_secret || "",
          sandbox_stripe_secret_key:      data.stripeConfig.sandbox_stripe_secret_key || "",
          sandbox_stripe_publishable_key: data.stripeConfig.sandbox_stripe_publishable_key || "",
          sandbox_stripe_webhook_secret:  data.stripeConfig.sandbox_stripe_webhook_secret || "",
        });
      }
    } catch (err) { setError(err.message); }
  }

  // Per-tab data loader. Pulled out so both onClick and the URL-state
  // useEffect below can call it without duplicating fetches.
  async function fetchTabData(t) {
    setError(""); setSuccess("");
    try {
      if (t === "members") { const d = await adminApi.getMembers(id, { limit: 50 }); setMembers(d.members || []); }
      else if (t === "logs") { const d = await adminApi.getLogs(id, { limit: 50 }); setLogs(d || []); }
      else if (t === "clients") { const d = await adminApi.getClients(id); setClients(d || []); }
      else if (t === "products") {
        const d = await adminApi.getVenueProducts(id); setProducts(d || []);
        if (venueForm.provider_type === "s7000") {
          try {
            const [at, ar] = await Promise.all([adminApi.getAnalysisTypes(id), adminApi.getAnalysisRules(id)]);
            setAnalysisTypes(at || []); setAnalysisRules(ar || []);
          } catch {}
        }
      }
    } catch (err) { setError(err.message); }
  }
  function loadTab(t) { setTab(t); fetchTabData(t); }
  // Re-fetch whenever the URL-restored tab changes (covers refresh /
  // back/forward / shared link). venueForm.provider_type is a dep
  // because the Products branch behaves differently for S7000 vs IGT.
  useEffect(() => {
    if (!id) return;
    fetchTabData(tab);
    // eslint-disable-next-line
  }, [id, tab, venueForm.provider_type]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(""), 3000); };

  async function loadSyncStatus() {
    try {
      const d = await adminApi.getSyncStatus(id);
      // New shape: { venue_id, syncs: [{key, last_sync_at, status, error, row_count, duration_ms}, ...] }
      // Old shape (for backwards-compat if deployed before 2.7.7): array of rows
      const list = Array.isArray(d) ? d : (d?.syncs || []);
      setSyncStatus(list);
    } catch {}
  }

  async function loadConnectionHealth() {
    setHealthLoading(true);
    try {
      const d = await adminApi.getConnectionHealth(id);
      setConnectionHealth(d);
    } catch (err) {
      console.error(err.message);
    } finally { setHealthLoading(false); }
  }

  async function importMemberNow(e) {
    e?.preventDefault();
    const raw = importMemberInput.trim().replace(/\s+/g, "");
    if (!raw) return;
    setImportMemberBusy(true); setImportMemberMsg(null);
    try {
      // 1-6 digits → membership number; 7+ digits → card number.
      const payload = /^\d{7,}$/.test(raw) ? { cardNumber: raw } : { membershipId: raw };
      const r = await adminApi.importMember(id, payload);
      setImportMemberMsg({
        ok: true,
        text: `Imported #${r.membernumber} · ${r.member.firstname} ${r.member.surname} · ${r.counts.cards} card(s), ${r.counts.analysis} tag(s)`,
      });
      setImportMemberInput("");
      loadSyncStatus();
    } catch (err) {
      setImportMemberMsg({ ok: false, text: err.message });
    }
    setImportMemberBusy(false);
  }

  async function loadCachedMembershipData() {
    if (!venue?.slug || venue?.provider_type !== "igt") return;
    try {
      const [types, terms] = await Promise.all([
        fetch(`/api/public/${venue.slug}/membership-types`).then(r => r.json()),
        fetch(`/api/public/${venue.slug}/membership-terms`).then(r => r.json()),
      ]);
      if (Array.isArray(types)) setCachedMemberTypes(types);
      if (Array.isArray(terms)) setCachedMemberTerms(terms);
    } catch {}
  }

  async function handleSyncNow() {
    setSyncing(true); setError("");
    try {
      await adminApi.syncNow(id);
      flash("Sync triggered successfully");
      await loadSyncStatus();
      await loadCachedMembershipData();
    } catch (err) { setError(err.message); }
    setSyncing(false);
  }

  // SwiftPOS reference data (classifications) — cached locally so the Default
  // Classification + product dropdowns don't require typing an id.
  async function loadSwiftposClassifications() {
    if (venue?.provider_type !== "swiftpos" && venueForm.provider_type !== "swiftpos") return;
    try {
      const d = await adminApi.getSwiftposClassifications(id);
      if (Array.isArray(d?.classifications)) setSwiftposClassifications(d.classifications);
    } catch {}
  }

  async function handleSwiftposSync() {
    setSwiftposSyncing(true); setError("");
    try {
      await adminApi.syncPart(id, "swiftpos");
      await loadSwiftposClassifications();
      flash("SwiftPOS reference data synced");
    } catch (err) { setError(err.message); }
    setSwiftposSyncing(false);
  }

  // CircleScan reference data (statuses/types/terms) — cached locally so the
  // default + per-product dropdowns don't require typing a code/id.
  async function loadCirclescanRefData() {
    if (venue?.provider_type !== "circlescan" && venueForm.provider_type !== "circlescan") return;
    try {
      const d = await adminApi.getCirclescanRefData(id);
      setCirclescanRefData({
        statuses: Array.isArray(d?.statuses) ? d.statuses : [],
        types:    Array.isArray(d?.types)    ? d.types    : [],
        terms:    Array.isArray(d?.terms)    ? d.terms    : [],
        sync:     d?.sync || null,
        observed: {
          statuses: d?.observed?.statuses || [],
          types:    d?.observed?.types || [],
          term_ids: d?.observed?.term_ids || [],
        },
      });
    } catch {}
  }

  // ── CircleScan reference-data maintenance ──────────────────────────
  const CS_REF_META = {
    statuses: { label: "Status", plural: "Statuses" },
    types:    { label: "Member Type", plural: "Member Types" },
    terms:    { label: "Term", plural: "Terms" },
  };

  function openCsRef(kind, item = null) {
    setCsRefModal({ kind, item });
    setCsRefError("");
    if (kind === "statuses") {
      setCsRefForm(item
        ? { code: item.code || "", description: item.description || "", is_valid: !!item.is_valid, is_default: !!item.is_default, is_active: item.is_active !== false }
        : { code: "", description: "", is_valid: false, is_default: false, is_active: true });
    } else if (kind === "types") {
      setCsRefForm(item
        ? { code: item.code || "", description: item.description || "", is_s2j: !!item.is_s2j, is_default: !!item.is_default, is_active: item.is_active !== false }
        : { code: "", description: "", is_s2j: false, is_default: false, is_active: true });
    } else {
      setCsRefForm(item
        ? { type_id: item.type_id ?? "", term: item.term ?? "", rate: item.rate || "", is_s2j: !!item.is_s2j, is_default: !!item.is_default, is_active: item.is_active !== false }
        : { type_id: "", term: "", rate: "", is_s2j: false, is_default: false, is_active: true });
    }
  }

  async function saveCsRef(e) {
    e.preventDefault();
    if (!csRefModal) return;
    const { kind, item } = csRefModal;
    // Member type is required for a term, but AdminSelect is a custom control so
    // the browser's native `required` can't enforce it — check it here.
    if (kind === "terms" && (csRefForm.type_id === "" || csRefForm.type_id == null)) {
      setCsRefError("Pick a member type for this term.");
      return;
    }
    setCsRefSaving(true); setCsRefError("");
    try {
      const refId = item ? (kind === "statuses" ? item.status_id : kind === "types" ? item.type_id : item.term_id) : null;
      if (item) await adminApi.updateCirclescanRefItem(id, kind, refId, csRefForm);
      else      await adminApi.createCirclescanRefItem(id, kind, csRefForm);
      await loadCirclescanRefData();
      setCsRefModal(null);
      flash(`${CS_REF_META[kind].label} ${item ? "updated" : "added"} in CircleScan`);
    } catch (err) { setCsRefError(err.message); }
    setCsRefSaving(false);
  }

  async function deleteCsRef(kind, item) {
    const name = kind === "terms" ? `term #${item.term_id}` : `"${item.code}"`;
    if (!window.confirm(`Delete ${name} from CircleScan? This affects the venue's live reference data.`)) return;
    setError("");
    try {
      const refId = kind === "statuses" ? item.status_id : kind === "types" ? item.type_id : item.term_id;
      await adminApi.deleteCirclescanRefItem(id, kind, refId);
      await loadCirclescanRefData();
      flash(`${CS_REF_META[kind].label} deleted`);
    } catch (err) { setError(err.message); }
  }

  async function handleCirclescanSync() {
    setCirclescanSyncing(true); setError("");
    try {
      await adminApi.syncPart(id, "circlescan");          // ref data (statuses/types/terms) — synchronous
      await loadCirclescanRefData();
      adminApi.syncPart(id, "circlescan_members").catch(() => {}); // full member sync — background
      flash("CircleScan reference data synced; member sync started");
    } catch (err) { setError(err.message); }
    setCirclescanSyncing(false);
  }

  async function saveVenue(e) {
    e.preventDefault(); setSaving(true); setError("");
    try { await adminApi.updateVenue(id, venueForm); flash("Venue details saved"); } catch (err) { setError(err.message); }
    setSaving(false);
  }

  async function saveProvider(e) {
    e.preventDefault(); setSaving(true); setError("");
    try {
      const payload =
        venueForm.provider_type === "igt"        ? { provider_type: "igt", ...igtForm } :
        venueForm.provider_type === "swiftpos"   ? { provider_type: "swiftpos", ...swiftposForm } :
        venueForm.provider_type === "circlescan" ? { provider_type: "circlescan", ...circlescanForm } :
                                                   { provider_type: "s7000", ...providerForm };
      await adminApi.updateProvider(id, payload);
      flash("Provider config saved");
    } catch (err) { setError(err.message); }
    setSaving(false);
  }

  async function saveStripe(e) {
    e.preventDefault(); setSaving(true); setError("");
    try { await adminApi.updateStripe(id, stripeForm); flash("Stripe config saved"); } catch (err) { setError(err.message); }
    setSaving(false);
  }

  async function testConnection() {
    setTestingConnection(true);
    setConnectionStatus(null);
    setTestResults(null);
    setError("");
    try {
      const result = await adminApi.testProvider(id);
      setTestResults(result);
      const allOk = result.checks.every((c) => c.status === "ok");
      setConnectionStatus(allOk ? "ok" : "fail");
    } catch (err) {
      setConnectionStatus("fail");
      setTestResults({ provider: venueForm.provider_type === "igt" ? "IGT Advantage" : venueForm.provider_type === "swiftpos" ? "SwiftPOS" : venueForm.provider_type === "circlescan" ? "CircleScan" : "Aristocrat S7000", checks: [{ name: "Connection", status: "fail", detail: err.message }], latency: 0 });
    }
    setTestingConnection(false);
  }

  async function addClient(e) {
    e.preventDefault(); setError("");
    try {
      await adminApi.createClient(id, clientForm);
      setClientForm({ email: "", password: "", name: "" });
      setShowInvite(false);
      flash("Client user created");
      const d = await adminApi.getClients(id);
      setClients(d || []);
    } catch (err) { setError(err.message); }
  }

  async function deleteClient(cid) {
    if (!window.confirm("Remove this client user? They will no longer be able to log in.")) return;
    try {
      await adminApi.deleteClient(id, cid);
      setClients((prev) => prev.filter((c) => c.id !== cid));
      flash("Client user removed");
    } catch (err) { setError(err.message); }
  }

  function openResetPw(c) {
    setResetPwTarget(c);
    setResetPwVal("");
    setShowResetPw(true);
  }

  async function resetClientPassword(e) {
    e.preventDefault();
    if (resetPwVal.length < 6) { setError("Password must be at least 6 characters"); return; }
    setSavingResetPw(true); setError("");
    try {
      await adminApi.changeClientPassword(id, resetPwTarget.id, resetPwVal);
      setShowResetPw(false);
      flash("Password updated");
    } catch (err) { setError(err.message); }
    setSavingResetPw(false);
  }

  async function openAddProduct() {
    setEditProduct(null);
    setProductForm({ name: "", description: "", price: "", currency: "aud", duration_months: 12, sync_to_stripe: false, analysis_codes: [], igt_membership_type_id: "", igt_term_id: "", swiftpos_classification_id: "", circlescan_type: "", circlescan_term_id: "" });
    setShowProductModal(true);
  }

  async function openEditProduct(p) {
    setEditProduct(p);
    setProductForm({ name: p.name, description: p.description || "", price: (p.price_cents / 100).toFixed(2), currency: p.currency || "aud", duration_months: p.duration_months || 12, sync_to_stripe: !!(p.stripe_product_id), analysis_codes: p.analysis_codes || [], igt_membership_type_id: p.igt_membership_type_id || "", igt_term_id: p.igt_term_id || "", swiftpos_classification_id: p.swiftpos_classification_id || "", circlescan_type: p.circlescan_type || "", circlescan_term_id: p.circlescan_term_id ?? "" });
    setShowProductModal(true);
  }

  async function saveProduct(e) {
    e.preventDefault();
    // IGT venues: both mappings are required — no fallback to venue defaults.
    if (venueForm.provider_type === "igt") {
      if (!productForm.igt_membership_type_id) { setError("Membership type required for IGT products"); return; }
      if (!productForm.igt_term_id)            { setError("Term required for IGT products");            return; }
    }
    setSavingProduct(true); setError("");
    try {
      const payload = { ...productForm, price_cents: Math.round(parseFloat(productForm.price || 0) * 100) };
      if (editProduct) {
        await adminApi.updateVenueProduct(id, editProduct.id, payload);
      } else {
        await adminApi.createVenueProduct(id, payload);
      }
      setShowProductModal(false);
      const d = await adminApi.getVenueProducts(id);
      setProducts(d || []);
      flash(editProduct ? "Product updated" : "Product created");
    } catch (err) { setError(err.message); }
    setSavingProduct(false);
  }

  async function deleteProduct(pid) {
    if (!window.confirm("Delete this product?")) return;
    try {
      await adminApi.deleteVenueProduct(id, pid);
      setProducts((p) => p.filter((x) => x.id !== pid));
      flash("Product deleted");
    } catch (err) { setError(err.message); }
  }

  /* ── IGT Import Wizard ──────────────────────────── */
  async function openImportWizard() {
    setShowImportWizard(true);
    setImportStep(0);
    setImportError("");
    setImportResults([]);
    setImportCandidates([]);
    try {
      const data = await adminApi.getIgtMembershipTypes(id);
      setIgtTypes(data.types || []);
      setIgtTerms(data.terms || []);
      const existing = data.existingProducts || [];

      // Build candidates: each type × each term
      // Pre-select only if the combination doesn't already exist as a product
      const candidates = [];
      for (const t of (data.types || [])) {
        for (const tm of (data.terms || [])) {
          const productName = `${t.name} — ${tm.name}`;
          const alreadyExists = existing.some(p =>
            p.name.toLowerCase() === productName.toLowerCase() ||
            (p.name.toLowerCase().includes(t.name.toLowerCase()) && p.duration_months === tm.duration_months)
          );
          candidates.push({
            key: `${t.id}-${tm.id}`,
            typeId: t.id,
            typeName: t.name,
            typeDescription: t.description,
            isPrimary: t.isPrimary,
            termId: tm.id,
            termName: tm.name,
            duration_months: tm.duration_months,
            selected: false,
            alreadyExists,
            price: "",
            name: productName,
          });
        }
      }
      setImportCandidates(candidates);
      setImportStep(1);
    } catch (err) {
      setImportError(err.message);
      setImportStep(1);
    }
  }

  function toggleCandidate(key) {
    setImportCandidates(prev => prev.map(c => c.key === key ? { ...c, selected: !c.selected } : c));
  }

  function toggleTypeAllTerms(typeId) {
    setImportCandidates(prev => {
      const typeItems = prev.filter(c => c.typeId === typeId && !c.alreadyExists);
      const allSelected = typeItems.every(c => c.selected);
      return prev.map(c => c.typeId === typeId && !c.alreadyExists ? { ...c, selected: !allSelected } : c);
    });
  }

  function setCandidateField(key, field, value) {
    setImportCandidates(prev => prev.map(c => c.key === key ? { ...c, [field]: value } : c));
  }

  function goToPricing() {
    const selected = importCandidates.filter(c => c.selected && !c.alreadyExists);
    if (selected.length === 0) { setImportError("Select at least one membership to import"); return; }
    setImportError("");
    setImportStep(2);
  }

  async function executeImport() {
    const toImport = importCandidates.filter(c => c.selected && !c.alreadyExists);
    const missing = toImport.filter(c => !c.price || parseFloat(c.price) <= 0);
    if (missing.length) { setImportError("Please set a price for all selected products"); return; }

    setImportStep(3);
    setImportError("");
    const results = [];

    for (const c of toImport) {
      try {
        await adminApi.createVenueProduct(id, {
          name: c.name,
          description: `${c.typeName} membership — ${c.termName}`,
          price_cents: Math.round(parseFloat(c.price) * 100),
          currency: "aud",
          duration_months: c.duration_months,
          sync_to_stripe: false,
          // Pre-map to the IGT type + term this candidate was built from,
          // so the product is ready to register members under the right
          // IGT membership without a second admin edit.
          igt_membership_type_id: c.typeId,
          igt_term_id:            c.termId,
        });
        results.push({ name: c.name, status: "ok", detail: `$${parseFloat(c.price).toFixed(2)} — ${c.duration_months ? c.duration_months + " months" : "Lifetime"}` });
      } catch (err) {
        results.push({ name: c.name, status: "fail", detail: err.message });
      }
    }

    setImportResults(results);
    setImportStep(4);

    try {
      const d = await adminApi.getVenueProducts(id);
      setProducts(d || []);
    } catch (_) {}
  }

  function closeImportWizard() {
    setShowImportWizard(false);
    setImportStep(0);
    setImportCandidates([]);
    setImportResults([]);
    setImportError("");
  }

  /* ── Check which setting sections have data filled ─── */
  const providerConfigured =
    venueForm.provider_type === "igt"        ? !!(igtForm.igt_srs_url && igtForm.igt_username) :
    venueForm.provider_type === "swiftpos"   ? !!(swiftposForm.swiftpos_integrator_name && swiftposForm.swiftpos_integrator_key) :
    venueForm.provider_type === "circlescan" ? !!(circlescanForm.circlescan_base_url && circlescanForm.circlescan_api_key) :
                                               !!(providerForm.s7000_base_url && providerForm.s7000_user_id);
  const stripeConfigured = !!(stripeForm.stripe_publishable_key && stripeForm.stripe_secret_key);

  const settingSections = [
    { id: "general",  label: "General",  icon: Icon.general,  status: venue?.name ? "done" : null },
    { id: "branding", label: "Branding", icon: Icon.branding, status: venueForm.logo_url ? "done" : null },
    { id: "terms",    label: "T&C", icon: Icon.logs, status: venueForm.terms_required ? "done" : null },
    { id: "comms",    label: "Comms", icon: Icon.mail, status: (venueForm.agm_enabled || venueForm.default_marketing_reason_id) ? "done" : null },
    { id: "portal-access", label: "Portal Access", icon: Icon.eye, status: (venueForm.hidden_menus?.length ? "done" : null) },
    { id: "provider", label: "Provider", icon: Icon.provider, status: providerConfigured ? "done" : "todo" },
    ...((venueForm.provider_type === "igt" || venueForm.provider_type === "s7000") ? [{ id: "tunnel", label: venueForm.provider_type === "igt" ? "IGT Tunnel" : "S7000 Tunnel", icon: Icon.zap, status: null }] : []),
    { id: "stripe",   label: "Stripe",   icon: Icon.stripe,   status: stripeConfigured ? "done" : "todo" },
    { id: "email-provider", label: "Email Provider", icon: Icon.mail, status: "done" },
    { id: "emails",   label: "Emails",   icon: Icon.mail,     status: "done" },
  ];

  if (!venue) return <AdminLayout><div className="loading-state"><div className="spinner" /></div></AdminLayout>;

  const tabDefs = [
    { key: "settings", label: "Settings",  icon: Icon.settings },
    { key: "members",  label: "Members",   icon: Icon.members },
    { key: "logs",     label: "Logs",      icon: Icon.logs },
    { key: "clients",  label: "Clients",   icon: Icon.clients },
    { key: "products", label: "Products",  icon: Icon.products },
    { key: "digitalcard", label: "Digital Card", icon: Icon.wallet },
    { key: "foxid",       label: "Licence Capture", icon: Icon.zap },
    { key: "api",         label: "API",          icon: Icon.zap },
    { key: "integration-tests", label: "Integration Tests", icon: Icon.zap },
    ...(venueForm.provider_type !== "igt" ? [{ key: "analysis", label: "Analysis Codes", icon: Icon.zap }] : []),
  ];

  return (
    <AdminLayout>
      {/* -- Page header -- */}
      <div className="venue-detail-header">
        <button className="btn btn-outline btn-sm" onClick={() => navigate("/admin/venues")} style={{ marginBottom: "0.75rem" }}>
          ← Back to Venues
        </button>
        <div className="venue-detail-title-row">
          <div className="venue-detail-avatar" style={{ background: venue.primary_color || "var(--accent)" }}>
            {venue.logo_url
              ? <img src={venue.logo_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit" }} onError={(e) => { e.target.style.display = "none"; }} />
              : venue.name?.charAt(0)
            }
          </div>
          <div style={{ flex: 1 }}>
            <h1 className="venue-detail-name">{venue.name}</h1>
            <div className="venue-detail-meta">
              <span className="venue-detail-slug">/{venue.slug}</span>
              <span className="venue-detail-dot">·</span>
              <span className="venue-detail-type-badge">{venueForm.provider_type === "igt" ? "IGT Advantage" : venueForm.provider_type === "swiftpos" ? "SwiftPOS" : venueForm.provider_type === "circlescan" ? "CircleScan" : "Aristocrat S7000"}</span>
            </div>
          </div>
        </div>
      </div>

      {error   && <div className="toast toast-error"   style={{ marginBottom: "1rem" }}>{error}</div>}
      {success && <div className="toast toast-success" style={{ marginBottom: "1rem" }}>{success}</div>}

      {/* Master live/sandbox toggle — drives the public signup form +
          Venue API v1 credential resolution. Integration Tests force
          sandbox regardless. */}
      <VenueModeToggle venue={venue} onChange={(mode) => setVenue((v) => ({ ...v, signup_mode: mode }))} />

      {/* -- Tab bar -- */}
      <div className="venue-tabs">
        {tabDefs.map((t) => (
          <button key={t.key} className={`venue-tab ${tab === t.key ? "active" : ""}`} onClick={() => loadTab(t.key)}>
            {t.icon}
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {/* ══ Settings Tab ══════════════════════════════════════ */}
      {tab === "settings" && (
        <div className="config-layout">
          <div className="config-nav">
            {settingSections.map((s) => (
              <button key={s.id} className={`config-nav-item ${activeSection === s.id ? "active" : ""}`} onClick={() => setActiveSection(s.id)} type="button">
                {s.icon}
                <span>{s.label}</span>
                {s.status === "done" && <span className="config-nav-dot done" />}
                {s.status === "todo" && <span className="config-nav-dot todo" />}
              </button>
            ))}
          </div>
          <div className="config-panel">
            {activeSection === "general" && (
              <form className="form-section" onSubmit={saveVenue}>
                <div className="section-header-row">
                  <div>
                    <h2>General Information</h2>
                    <p className="section-desc">Basic venue details and identity</p>
                  </div>
                </div>
                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Venue Name</label>
                    <input value={venueForm.name} onChange={(e) => setVenueForm({ ...venueForm, name: e.target.value })} />
                  </div>
                  <div className="form-field">
                    <label>URL Slug</label>
                    <div className="input-prefix-wrap">
                      <span className="input-prefix">/</span>
                      <input value={venueForm.slug} onChange={(e) => setVenueForm({ ...venueForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} />
                    </div>
                  </div>
                </div>
                <div className="form-field">
                  <label>Public Signup URL</label>
                  <div className="url-display-row">
                    <a href={`/${venue.slug}`} target="_blank" rel="noreferrer" className="url-display-link">
                      {window.location.origin}/{venue.slug}
                      {Icon.externalLink}
                    </a>
                    <CopyButton text={`${window.location.origin}/${venue.slug}`} />
                  </div>
                </div>
                <div className="form-field" style={{ marginTop: "0.5rem" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={!!venueForm.duplicate_check_enabled}
                      onChange={(e) => setVenueForm({ ...venueForm, duplicate_check_enabled: e.target.checked })}
                    />
                    <span>Duplicate check on new joins</span>
                  </label>
                  <p className="field-hint">When on, the public signup checks new joiners by name + date of birth against existing members. On a match it blocks the join and offers to switch to the renewal flow (renew with the membership number).</p>
                </div>
                <div className="form-field" style={{ marginTop: "0.75rem" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={venueForm.signup_enabled !== false}
                      onChange={(e) => setVenueForm({ ...venueForm, signup_enabled: e.target.checked })}
                    />
                    <span>Allow new signups</span>
                  </label>
                  <p className="field-hint">When off, new members can't join through the public form — the join path is hidden and the server refuses new memberships. Existing members can still renew if renewals are enabled.</p>
                </div>
                <div className="form-field" style={{ marginTop: "0.5rem" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={venueForm.renewal_enabled !== false}
                      onChange={(e) => setVenueForm({ ...venueForm, renewal_enabled: e.target.checked })}
                    />
                    <span>Allow renewals</span>
                  </label>
                  <p className="field-hint">When off, existing members can't renew through the public form — the renewal path is hidden and the server refuses renewals. New signups can still proceed if enabled.</p>
                </div>
                <div className="form-field" style={{ marginTop: "0.5rem" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={venueForm.digital_passes_enabled !== false}
                      onChange={(e) => setVenueForm({ ...venueForm, digital_passes_enabled: e.target.checked })}
                    />
                    <span>Digital membership (Litecard)</span>
                  </label>
                  <p className="field-hint">When off, the venue portal hides the Digital Passes + Digital Card menus and the member Passes tab. Venue admins can also change this in their portal under Settings → Portal.</p>
                </div>
                <div className="form-field" style={{ marginTop: "0.5rem" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={venueForm.gender_field_enabled !== false}
                      onChange={(e) => setVenueForm({ ...venueForm, gender_field_enabled: e.target.checked })}
                    />
                    <span>Show the Gender field on signup</span>
                  </label>
                  <p className="field-hint">When off, the Gender dropdown is removed from the public signup form and no gender is sent to the provider. (Gender is optional at the provider.)</p>
                </div>
                <div className="form-field" style={{ marginTop: "0.5rem" }}>
                  <label>GA4 Measurement ID</label>
                  <input
                    type="text"
                    value={venueForm.ga4_measurement_id || ""}
                    onChange={(e) => setVenueForm({ ...venueForm, ga4_measurement_id: e.target.value })}
                    placeholder="G-XXXXXXXXXX"
                  />
                  <p className="field-hint">Optional. Fires Google Analytics 4 from the public signup — a page view on load and a <code>sign_up</code> conversion on completion. Leave blank to disable. Loads a third-party tracker and sets cookies — the venue is responsible for its own cookie-consent notice.</p>
                </div>
                <div className="form-field" style={{ marginTop: "0.5rem" }}>
                  <label>Meta Pixel ID</label>
                  <input
                    type="text"
                    value={venueForm.meta_pixel_id || ""}
                    onChange={(e) => setVenueForm({ ...venueForm, meta_pixel_id: e.target.value })}
                    placeholder="e.g. 123456789012345"
                  />
                  <p className="field-hint">Optional. Fires the Meta (Facebook) Pixel from the public signup — PageView on load and CompleteRegistration on completion. Leave blank to disable.</p>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : "Save Changes"}</button>
                </div>

                {/* Connection Health — all integrations at a glance */}
                <div style={{ marginTop: "2rem" }}>
                  <div className="form-section-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <span>Connection Health</span>
                    <button type="button" className="btn btn-outline btn-sm" onClick={loadConnectionHealth} disabled={healthLoading}>
                      {healthLoading ? "Checking…" : "Refresh"}
                    </button>
                  </div>
                  {!connectionHealth && !healthLoading && (
                    <div style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>Click Refresh to check all integrations.</div>
                  )}
                  {connectionHealth && (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "0.5rem" }}>
                      {connectionHealth.integrations.map((r) => (
                        <div key={r.key} style={{
                          padding: "0.75rem 0.9rem", borderRadius: 8,
                          border: `1px solid ${r.ok ? "rgba(16,185,129,0.3)" : "rgba(239,68,68,0.3)"}`,
                          background: r.ok ? "rgba(16,185,129,0.05)" : "rgba(239,68,68,0.05)",
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontWeight: 600 }}>{r.label}</span>
                            <span style={{
                              fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.05em",
                              color: r.ok ? "var(--success)" : "var(--danger)",
                            }}>
                              {r.ok ? "OK" : "ISSUE"}
                            </span>
                          </div>
                          <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>{r.detail}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </form>
            )}

            {activeSection === "branding" && (
              <form className="form-section" onSubmit={saveVenue}>
                <div className="section-header-row">
                  <div>
                    <h2>Branding</h2>
                    <p className="section-desc">Customize the public signup page appearance</p>
                  </div>
                </div>
                <div className="form-grid-2">
                  <ImageUploadField label="Logo" hint="Square or horizontal logo recommended" value={venueForm.logo_url} onChange={(v) => setVenueForm({ ...venueForm, logo_url: v })} />
                  <ImageUploadField label="Cover / Hero Image" hint="Wide landscape image for the signup header" value={venueForm.cover_image_url} onChange={(v) => setVenueForm({ ...venueForm, cover_image_url: v })} />
                </div>
                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Primary Color</label>
                    <ColorPicker value={venueForm.primary_color} onChange={(v) => setVenueForm({ ...venueForm, primary_color: v })} />
                    <span className="field-hint">Buttons &amp; links</span>
                  </div>
                  <div className="form-field">
                    <label>Accent Color</label>
                    <ColorPicker value={venueForm.accent_color} onChange={(v) => setVenueForm({ ...venueForm, accent_color: v })} />
                    <span className="field-hint">Header &amp; footer</span>
                  </div>
                </div>
                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Background Color</label>
                    <ColorPicker value={venueForm.background_color} onChange={(v) => setVenueForm({ ...venueForm, background_color: v })} />
                    <span className="field-hint">Page background</span>
                  </div>
                  <div className="form-field">
                    <label>Text Color</label>
                    <ColorPicker value={venueForm.text_color} onChange={(v) => setVenueForm({ ...venueForm, text_color: v })} />
                    <span className="field-hint">Body text</span>
                  </div>
                </div>
                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Button Text Color</label>
                    <ColorPicker value={venueForm.button_text_color} onChange={(v) => setVenueForm({ ...venueForm, button_text_color: v })} />
                    <span className="field-hint">Text on primary buttons</span>
                  </div>
                  <div className="form-field"><label>Button Radius — {venueForm.button_radius}px</label><input type="range" min="0" max="24" value={venueForm.button_radius} onChange={(e) => setVenueForm({ ...venueForm, button_radius: parseInt(e.target.value) })} /></div>
                </div>
                <div className="form-grid-2">
                  <div className="form-field"><label>Font Family</label><AdminSelect value={venueForm.font_family} onChange={(v) => setVenueForm({ ...venueForm, font_family: v })} searchable searchPlaceholder="Search fonts..." options={[{value:"Inter",label:"Inter"},{value:"Roboto",label:"Roboto"},{value:"Open Sans",label:"Open Sans"},{value:"Poppins",label:"Poppins"},{value:"Montserrat",label:"Montserrat"},{value:"Lato",label:"Lato"},{value:"DM Sans",label:"DM Sans"},{value:"Nunito",label:"Nunito"},{value:"Source Sans 3",label:"Source Sans 3"}]} /></div>
                  <div className="form-field"></div>
                </div>

                <div className="form-section-label" style={{ marginTop: "1rem" }}>Layout &amp; hero</div>
                <p className="section-desc" style={{ marginTop: 0, marginBottom: "0.75rem" }}>
                  Show or hide the branded hero panel, choose its side, width and contents, and set the shell corners &amp; form alignment.
                </p>
                <SignupLayoutControls value={venueForm.signup_layout} onChange={(v) => setVenueForm({ ...venueForm, signup_layout: v })} />

                <div className="form-section-label" style={{ marginTop: "1rem" }}>Signup text</div>
                <p className="section-desc" style={{ marginTop: 0, marginBottom: "0.75rem" }}>
                  Each line can be switched off, and given its own font and colour. Leave font/colour blank to inherit the venue styles above.
                </p>
                {SIGNUP_TEXT_SLOTS.map((slot) => (
                  <SignupTextRow
                    key={slot.key}
                    slot={slot}
                    value={venueForm[slot.key] || ""}
                    onValue={(v) => setVenueForm({ ...venueForm, [slot.key]: v })}
                    style={(venueForm.signup_text_styles || {})[slot.key] || {}}
                    onStyle={(st) => setVenueForm({ ...venueForm, signup_text_styles: { ...(venueForm.signup_text_styles || {}), [slot.key]: st } })}
                    placeholder={{
                      welcome_title: "Welcome to Our Club",
                      welcome_subtitle: "Sign up for exclusive benefits",
                      choice_title: "Welcome!",
                      choice_subtitle: "Are you an existing member or joining for the first time?",
                    }[slot.key]}
                  />
                ))}

                <div className="form-section-label" style={{ marginTop: "1rem" }}>Signup benefits</div>
                <p className="section-desc" style={{ marginTop: 0, marginBottom: "0.75rem" }}>
                  The value props listed in the signup hero panel.
                </p>
                <SignupBenefitsEditor value={venueForm.signup_benefits} onChange={(v) => setVenueForm({ ...venueForm, signup_benefits: v })} />

                <h3 style={{ marginTop: "1.5rem", marginBottom: "0.75rem", fontSize: "0.95rem", fontWeight: 600 }}>Live Preview</h3>
                <SignupLivePreview venue={venueForm} slug={venueForm.slug} />
                <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: "0.5rem" }}>
                  Your real signup — interactive and updating as you edit.
                </p>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : "Save Branding"}</button>
                </div>
              </form>
            )}

            {activeSection === "terms" && (
              <form className="form-section" onSubmit={saveVenue}>
                <div className="section-header-row">
                  <div>
                    <h2>Terms &amp; Conditions</h2>
                    <p className="section-desc">
                      When enabled, members must read, tick, and sign an
                      acknowledgement before they can pay. Acceptance is stored
                      on the member record and in the activity log for audit.
                    </p>
                  </div>
                </div>

                <div className="form-field">
                  <label className="checkbox-row" style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={!!venueForm.terms_required}
                      onChange={(e) => setVenueForm({ ...venueForm, terms_required: e.target.checked })}
                    />
                    <span>Require T&amp;Cs acknowledgement during public signup</span>
                  </label>
                </div>

                <div className="form-field">
                  <label className="checkbox-row" style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: venueForm.terms_required ? "pointer" : "not-allowed", opacity: venueForm.terms_required ? 1 : 0.55 }}>
                    <input
                      type="checkbox"
                      checked={venueForm.terms_require_signature !== false}
                      onChange={(e) => setVenueForm({ ...venueForm, terms_require_signature: e.target.checked })}
                      disabled={!venueForm.terms_required}
                    />
                    <span>Require a signature (uncheck for a tick-only acknowledgement)</span>
                  </label>
                </div>

                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Heading</label>
                    <input
                      value={venueForm.terms_title}
                      onChange={(e) => setVenueForm({ ...venueForm, terms_title: e.target.value })}
                      placeholder="Membership Terms & Conditions"
                    />
                    <span className="field-hint">Title shown at the top of the Terms step</span>
                  </div>
                  <div className="form-field">
                    <label>Version</label>
                    <input
                      value={venueForm.terms_version}
                      onChange={(e) => setVenueForm({ ...venueForm, terms_version: e.target.value })}
                      placeholder="v1 / 2026-05"
                    />
                    <span className="field-hint">Bump this whenever the body text changes — captured on every acknowledgement</span>
                  </div>
                </div>

                {/* Either field works on its own. The public signup form
                    auto-detects: if Body Text is filled it renders the text
                    inline; if only the External Link is set it renders an
                    "Open Terms & Conditions" button instead. */}
                <div className="form-field">
                  <label>Body Text</label>
                  <textarea
                    rows={12}
                    value={venueForm.terms_body}
                    onChange={(e) => setVenueForm({ ...venueForm, terms_body: e.target.value })}
                    placeholder="Paste the full Terms & Conditions text here. Two blank lines separate paragraphs. Leave blank to use the External Link below instead."
                    style={{ width: "100%", fontFamily: "inherit", fontSize: "0.9rem", lineHeight: "1.5", padding: "0.65rem 0.8rem", borderRadius: "8px", border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text)", resize: "vertical" }}
                  />
                  <span className="field-hint">If empty, the form will link out to the External Link below instead.</span>
                </div>

                <div className="form-field">
                  <label>External Link {venueForm.terms_body ? "(optional)" : ""}</label>
                  <input
                    type="url"
                    value={venueForm.terms_url}
                    onChange={(e) => {
                      const url = e.target.value;
                      // Auto-flip to new_tab as soon as the link looks
                      // like a PDF so the saved value matches the
                      // disabled UI state.
                      const isPdf = /\.pdf(\?|#|$)/i.test(url);
                      setVenueForm({
                        ...venueForm,
                        terms_url: url,
                        ...(isPdf ? { terms_display_mode: "new_tab" } : {}),
                      });
                    }}
                    placeholder="https://yourvenue.com.au/membership-terms"
                  />
                  <span className="field-hint">
                    {venueForm.terms_body
                      ? "Shown below the body text — useful for linking the full PDF."
                      : "Used as the primary Terms source when no Body Text is provided."}
                  </span>
                </div>

                {/* Display-mode picker — only relevant when a URL is set
                    (the inline body always renders in-page). PDFs are
                    locked to new-tab because most browsers block iframe
                    PDF embedding. */}
                {venueForm.terms_url && (() => {
                  const isPdf = /\.pdf(\?|#|$)/i.test(venueForm.terms_url || "");
                  return (
                    <div className="form-field">
                      <label>How should the link open?</label>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", marginTop: "0.4rem" }}>
                        <label
                          style={{
                            display: "flex", alignItems: "flex-start", gap: "0.6rem",
                            padding: "0.7rem 0.85rem", borderRadius: "8px",
                            border: `1px solid ${venueForm.terms_display_mode === "new_tab" ? "var(--accent)" : "var(--border)"}`,
                            background: venueForm.terms_display_mode === "new_tab" ? "var(--accent-soft)" : "var(--bg-input)",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="radio"
                            name="terms_display_mode"
                            value="new_tab"
                            checked={venueForm.terms_display_mode === "new_tab"}
                            onChange={() => setVenueForm({ ...venueForm, terms_display_mode: "new_tab" })}
                            style={{ marginTop: "2px" }}
                          />
                          <div>
                            <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>New tab</div>
                            <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: "0.15rem" }}>
                              Opens in a fresh browser tab. Works for everything, including PDFs.
                            </div>
                          </div>
                        </label>
                        <label
                          title={isPdf ? "PDFs cannot be embedded in a modal — must open in a new tab." : ""}
                          style={{
                            display: "flex", alignItems: "flex-start", gap: "0.6rem",
                            padding: "0.7rem 0.85rem", borderRadius: "8px",
                            border: `1px solid ${(!isPdf && venueForm.terms_display_mode === "modal") ? "var(--accent)" : "var(--border)"}`,
                            background: (!isPdf && venueForm.terms_display_mode === "modal") ? "var(--accent-soft)" : "var(--bg-input)",
                            cursor: isPdf ? "not-allowed" : "pointer",
                            opacity: isPdf ? 0.55 : 1,
                          }}
                        >
                          <input
                            type="radio"
                            name="terms_display_mode"
                            value="modal"
                            checked={!isPdf && venueForm.terms_display_mode === "modal"}
                            disabled={isPdf}
                            onChange={() => setVenueForm({ ...venueForm, terms_display_mode: "modal" })}
                            style={{ marginTop: "2px" }}
                          />
                          <div>
                            <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                              In-page modal {isPdf && <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>(unavailable for PDFs)</span>}
                            </div>
                            <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: "0.15rem" }}>
                              Loads the page inside an overlay so the member never leaves the form.
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                  );
                })()}

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : "Save Terms"}</button>
                </div>
              </form>
            )}

            {activeSection === "comms" && (
              <div className="form-section">
                <div className="section-header-row">
                  <div>
                    <h2>Communications</h2>
                    <p className="section-desc">
                      AGM acknowledgement copy and marketing channels for
                      the public signup comms step. For IGT venues, the
                      channel list is mirrored from the provider and the
                      marketing-reason ID is the rationale stamped on
                      every member-marketing PUT.
                    </p>
                  </div>
                </div>

                {/* AGM ── per-venue copy */}
                <form onSubmit={saveVenue}>
                  <div className="form-field">
                    <label className="checkbox-row" style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={venueForm.comms_step_enabled !== false}
                        onChange={(e) => setVenueForm({ ...venueForm, comms_step_enabled: e.target.checked })}
                      />
                      <span>Show the Communications step during public signup</span>
                    </label>
                    <span className="field-hint">Off = the whole Comms page (AGM + marketing opt-ins) is skipped, regardless of the settings below.</span>
                  </div>

                  <h3 style={{ margin: "1rem 0 0.4rem", fontSize: "0.95rem" }}>AGM acknowledgement</h3>
                  <div className="form-field">
                    <label className="checkbox-row" style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={!!venueForm.agm_enabled}
                        onChange={(e) => setVenueForm({ ...venueForm, agm_enabled: e.target.checked })}
                      />
                      <span>Show AGM acknowledgement on the signup form</span>
                    </label>
                  </div>
                  <div className="form-field">
                    <label>Version label</label>
                    <input
                      value={venueForm.agm_version}
                      onChange={(e) => setVenueForm({ ...venueForm, agm_version: e.target.value })}
                      placeholder="2026-05"
                      style={{ maxWidth: 220 }}
                    />
                    <span className="field-hint">Bump whenever the copy below changes — captured per acknowledgement.</span>
                  </div>
                  <div className="form-field">
                    <label>Copy</label>
                    <textarea
                      rows={5}
                      value={venueForm.agm_copy}
                      onChange={(e) => setVenueForm({ ...venueForm, agm_copy: e.target.value })}
                      placeholder="By joining, I acknowledge that I will receive Annual General Meeting notices from the venue. This notification cannot be disabled."
                      style={{ width: "100%", fontFamily: "inherit", fontSize: "0.9rem", padding: "0.65rem 0.8rem", borderRadius: "8px", border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text)", resize: "vertical" }}
                    />
                  </div>

                  {/* Marketing reason mapping — IGT only */}
                  {venueForm.provider_type === "igt" && (
                    <>
                      <h3 style={{ margin: "1.25rem 0 0.4rem", fontSize: "0.95rem" }}>Marketing reason (IGT)</h3>
                      <p className="section-desc" style={{ marginBottom: "0.75rem" }}>
                        IGT requires a reason ID on every marketing
                        opt-in change. Pick the default; override per join
                        type if you want reports to distinguish new joins
                        from renewals.
                      </p>
                      {commsReasons.length === 0 ? (
                        <div className="toast toast-warning" style={{ marginBottom: "0.75rem" }}>
                          No marketing reasons synced yet. IGT needs at
                          least one Direct Marketing Reason configured
                          for this site, then run a sync. Opt-in writes
                          to IGT are skipped until then.
                        </div>
                      ) : (
                        <div className="form-grid-3">
                          {[
                            ["default_marketing_reason_id", "Default"],
                            ["marketing_reason_id_new",     "New joins (override)"],
                            ["marketing_reason_id_renewal", "Renewals (override)"],
                          ].map(([key, label]) => (
                            <div key={key} className="form-field">
                              <label>{label}</label>
                              <AdminSelect
                                value={venueForm[key] != null ? String(venueForm[key]) : ""}
                                onChange={(v) => setVenueForm({ ...venueForm, [key]: v })}
                                placeholder="— Not set —"
                                options={[
                                  { value: "", label: "— Not set —" },
                                  ...commsReasons.map((r) => ({
                                    value: String(r.marketing_reason_id),
                                    label: `${r.marketing_reason_id} — ${r.name}`,
                                  })),
                                ]}
                              />

                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : "Save Comms Settings"}</button>
                  </div>
                </form>

                {/* Channels ── separate panel, manages its own state */}
                <h3 style={{ margin: "1.5rem 0 0.4rem", fontSize: "0.95rem" }}>Marketing channels</h3>
                <p className="section-desc" style={{ marginBottom: "0.75rem" }}>
                  {venueForm.provider_type === "igt"
                    ? "Synced from IGT. Toggle channels off here to hide them from the signup form without changing the provider. Names are managed in IGT."
                    : "Custom list — stored locally. S7000 doesn't expose marketing channels, so the venue defines its own here."}
                </p>

                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--border)" }}>
                      <th style={{ textAlign: "left", padding: "0.5rem 0.4rem", fontSize: "0.82rem" }}>Channel</th>
                      <th style={{ textAlign: "left", padding: "0.5rem 0.4rem", fontSize: "0.82rem", width: 120 }}>Source</th>
                      <th style={{ textAlign: "left", padding: "0.5rem 0.4rem", fontSize: "0.82rem", width: 110 }}>Active</th>
                      <th style={{ width: 80 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {commsOptions.length === 0 && (
                      <tr><td colSpan={4} style={{ padding: "0.75rem 0.4rem", color: "var(--text-muted)" }}>No marketing options configured.</td></tr>
                    )}
                    {commsOptions.map((o) => (
                      <tr key={o.id} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: "0.5rem 0.4rem" }}>{o.name}</td>
                        <td style={{ padding: "0.5rem 0.4rem", color: "var(--text-muted)", fontSize: "0.85rem" }}>
                          {o.source === "igt_sync" ? "IGT" : "Custom"}
                        </td>
                        <td style={{ padding: "0.5rem 0.4rem" }}>
                          <label style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              checked={!!o.active}
                              onChange={async (e) => {
                                try {
                                  const updated = await adminApi.updateVenueMarketingOption(id, o.id, { active: e.target.checked });
                                  setCommsOptions((cur) => cur.map((x) => x.id === o.id ? updated : x));
                                } catch (err) { alert(err.message); }
                              }}
                            />
                            <span style={{ fontSize: "0.85rem" }}>{o.active ? "Visible" : "Hidden"}</span>
                          </label>
                        </td>
                        <td style={{ padding: "0.5rem 0.4rem", textAlign: "right" }}>
                          {o.source === "venue_custom" && (
                            <button
                              className="btn btn-outline btn-sm"
                              type="button"
                              onClick={async () => {
                                if (!window.confirm("Delete this marketing option? Existing member opt-ins for it will also be removed.")) return;
                                try {
                                  await adminApi.deleteVenueMarketingOption(id, o.id);
                                  setCommsOptions((cur) => cur.filter((x) => x.id !== o.id));
                                } catch (err) { alert(err.message); }
                              }}
                            >Delete</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {venueForm.provider_type !== "igt" && (
                  <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem" }}>
                    <input
                      value={newCommsOption}
                      onChange={(e) => setNewCommsOption(e.target.value)}
                      placeholder="New channel name (e.g. Email)"
                      style={{ flex: 1 }}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter" || !newCommsOption.trim()) return;
                        e.preventDefault();
                        adminApi.createVenueMarketingOption(id, { name: newCommsOption.trim(), active: true })
                          .then((created) => { setCommsOptions((cur) => [...cur, created]); setNewCommsOption(""); })
                          .catch((err) => alert(err.message));
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={!newCommsOption.trim()}
                      onClick={() => {
                        adminApi.createVenueMarketingOption(id, { name: newCommsOption.trim(), active: true })
                          .then((created) => { setCommsOptions((cur) => [...cur, created]); setNewCommsOption(""); })
                          .catch((err) => alert(err.message));
                      }}
                    >
                      Add channel
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeSection === "portal-access" && (() => {
              const items  = hideableMenusForProvider(venueForm.provider_type);
              const hidden = new Set(venueForm.hidden_menus || []);
              const setHidden = (next) => setVenueForm({ ...venueForm, hidden_menus: [...next] });
              const toggle = (key) => {
                const next = new Set(venueForm.hidden_menus || []);
                if (next.has(key)) next.delete(key); else next.add(key);
                setHidden(next);
              };
              return (
                <form className="form-section" onSubmit={saveVenue}>
                  <div className="section-header-row">
                    <div>
                      <h2>Portal Access</h2>
                      <p className="section-desc">Choose which menus appear in this venue's portal sidebar. Switch off anything that isn't relevant — admins and staff simply won't see it.</p>
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => setHidden(new Set())}>Show all</button>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => setHidden(new Set(items.map((m) => m.key)))}>Hide all</button>
                  </div>

                  {["main", "reports", "settings"].map((g) => {
                    const groupItems = items.filter((m) => m.group === g);
                    if (!groupItems.length) return null;
                    return (
                      <div key={g} style={{ marginBottom: "1.25rem" }}>
                        <div className="form-section-label">{MENU_GROUP_LABELS[g]}</div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "0.4rem" }}>
                          {groupItems.map((m) => {
                            const visible = !hidden.has(m.key);
                            return (
                              <label key={m.key} style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.5rem 0.7rem", borderRadius: 8, border: "1px solid var(--border)", cursor: "pointer", background: visible ? "transparent" : "var(--bg-elevated)" }}>
                                <input type="checkbox" checked={visible} onChange={() => toggle(m.key)} />
                                <span style={{ fontWeight: 500, color: visible ? "var(--text)" : "var(--text-muted)" }}>{m.label}</span>
                                {m.adminOnly && <span style={{ marginLeft: "auto", fontSize: "0.68rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Admin</span>}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  <p className="field-hint" style={{ marginTop: "0.5rem" }}>
                    Dashboard, Members and My Profile are always visible. Digital membership menus (Digital Passes, Digital Card) are controlled by the “Digital membership (Litecard)” toggle under General.
                  </p>

                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : "Save Changes"}</button>
                  </div>
                </form>
              );
            })()}

            {activeSection === "provider" && (
              <form className="form-section" onSubmit={saveProvider}>
                <div className="section-header-row">
                  <div>
                    <h2>Provider Configuration</h2>
                    <p className="section-desc">Gaming system integration for member registration</p>
                  </div>
                  {connectionStatus === "ok" && <span className="connection-badge connected">{Icon.check} Connected</span>}
                  {connectionStatus === "fail" && <span className="connection-badge failed">Connection failed</span>}
                </div>

                <div className="form-field" style={{ marginBottom: "1.5rem" }}>
                  <label>Provider Type</label>
                  <AdminSelect value={venueForm.provider_type} onChange={(v) => { setVenueForm({ ...venueForm, provider_type: v }); setConnectionStatus(null); }} options={[{value:"s7000",label:"Aristocrat S7000"},{value:"igt",label:"IGT Advantage"},{value:"swiftpos",label:"SwiftPOS"},{value:"circlescan",label:"CircleScan"}]} />
                </div>

                {/* Live / Sandbox sub-tab — bindings below switch via pKey() */}
                <EnvironmentSubTabs value={providerEnv} onChange={setProviderEnv} />

                {venueForm.provider_type === "s7000" && (
                  <>
                    <div className="form-section-label">Endpoint</div>
                    <div className="form-field">
                      <label>API Base URL</label>
                      <input value={providerForm[pKey("s7000_base_url")] || ""} onChange={(e) => setProviderForm({ ...providerForm, [pKey("s7000_base_url")]: e.target.value })} placeholder="http://system7000api.aristocratgaming.com:8444" />
                    </div>
                    <div className="form-section-label" style={{ marginTop: "0.5rem" }}>Authentication</div>
                    <div className="form-field">
                      <label>Registration Code</label>
                      <SecretInput value={providerForm[pKey("s7000_reg_code")] || ""} onChange={(e) => setProviderForm({ ...providerForm, [pKey("s7000_reg_code")]: e.target.value })} />
                    </div>
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>User ID</label>
                        <input value={providerForm[pKey("s7000_user_id")] || ""} onChange={(e) => setProviderForm({ ...providerForm, [pKey("s7000_user_id")]: e.target.value })} />
                      </div>
                      <div className="form-field">
                        <label>Password</label>
                        <SecretInput value={providerForm[pKey("s7000_password")] || ""} onChange={(e) => setProviderForm({ ...providerForm, [pKey("s7000_password")]: e.target.value })} />
                      </div>
                    </div>
                  </>
                )}

                {venueForm.provider_type === "igt" && (
                  <>
                    <div className="form-section-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <span>Service Endpoints</span>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => setIgtForm({
                          ...igtForm,
                          igt_srs_url:  "http://igtreverseproxy.australiaeast.cloudapp.azure.com:8011/club/1.0.0",
                          igt_mers_url: "http://igtreverseproxy.australiaeast.cloudapp.azure.com:8091/club/1.0.0/members",
                        })}
                        title="Pre-fill the Circle Solutions Azure reverse-proxy URLs (no VPN required)"
                      >
                        Use reverse-proxy defaults
                      </button>
                    </div>
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>SRS URL <span className="field-hint-inline">Security</span></label>
                        <input value={igtForm[pKey("igt_srs_url")] || ""} onChange={(e) => setIgtForm({ ...igtForm, [pKey("igt_srs_url")]: e.target.value })} placeholder="http://igtreverseproxy.australiaeast.cloudapp.azure.com:8011/club/1.0.0" />
                      </div>
                      <div className="form-field">
                        <label>MeRS URL <span className="field-hint-inline">Membership</span></label>
                        <input value={igtForm[pKey("igt_mers_url")] || ""} onChange={(e) => setIgtForm({ ...igtForm, [pKey("igt_mers_url")]: e.target.value })} placeholder="http://igtreverseproxy.australiaeast.cloudapp.azure.com:8091/club/1.0.0/members" />
                      </div>
                    </div>

                    <div className="form-section-label" style={{ marginTop: "0.5rem" }}>Authentication</div>
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>Username</label>
                        <input value={igtForm[pKey("igt_username")] || ""} onChange={(e) => setIgtForm({ ...igtForm, [pKey("igt_username")]: e.target.value })} />
                      </div>
                      <div className="form-field">
                        <label>Password</label>
                        <SecretInput value={igtForm[pKey("igt_password")] || ""} onChange={(e) => setIgtForm({ ...igtForm, [pKey("igt_password")]: e.target.value })} />
                      </div>
                    </div>
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>External ID</label>
                        <input value={igtForm[pKey("igt_external_id")] || ""} onChange={(e) => setIgtForm({ ...igtForm, [pKey("igt_external_id")]: e.target.value })} />
                      </div>
                      <div className="form-field">
                        <label>HMAC Key <span className="field-hint-inline">Base64</span></label>
                        <SecretInput value={igtForm[pKey("igt_mac_key")] || ""} onChange={(e) => setIgtForm({ ...igtForm, [pKey("igt_mac_key")]: e.target.value })} />
                      </div>
                    </div>
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>Validation Type <span className="field-hint-inline">HMAC algorithm</span></label>
                        <AdminSelect
                          value={igtForm[pKey("igt_validation_type")] || "HMAC SHA512"}
                          onChange={(v) => setIgtForm({ ...igtForm, [pKey("igt_validation_type")]: v })}
                          options={[
                            { value: "HMAC SHA512", label: "HMAC SHA512 (entity 4434 — standard)" },
                            { value: "HMAC SHA256", label: "HMAC SHA256 (entity 4435)" },
                          ]}
                        />
                      </div>
                      <div className="form-field">
                        <label>Attendant Code <span className="field-hint-inline">optional</span></label>
                        <input value={igtForm[pKey("igt_attendant_code")] || ""} onChange={(e) => setIgtForm({ ...igtForm, [pKey("igt_attendant_code")]: e.target.value })} placeholder="e.g. 12345656" />
                      </div>
                    </div>

                    <div className="form-section-label" style={{ marginTop: "0.5rem" }}>Membership Defaults</div>
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>Site ID</label>
                        <input type="number" min="0" value={igtForm.igt_site_id} onChange={(e) => { const n = parseInt(e.target.value, 10); setIgtForm({ ...igtForm, igt_site_id: Number.isNaN(n) ? "" : n }); }} />
                      </div>
                      <div className="form-field">
                        <label>Membership Type</label>
                        <AdminSelect value={igtForm.igt_membership_type_id} onChange={(v) => setIgtForm({ ...igtForm, igt_membership_type_id: parseInt(v) })} options={cachedMemberTypes.length > 0 ? cachedMemberTypes.map(t => ({ value: t.membership_type_id, label: `${t.membership_type_id} — ${t.name}` })) : [{value:1,label:"1 — Life Members"},{value:2,label:"2 — Club RSL Full"},{value:3,label:"3 — Visitor"}]} />
                      </div>
                    </div>
                    <div className="form-field">
                      <label>Term</label>
                      <AdminSelect value={igtForm.igt_term_id} onChange={(v) => setIgtForm({ ...igtForm, igt_term_id: parseInt(v) })} options={cachedMemberTerms.length > 0 ? cachedMemberTerms.map(t => ({ value: t.term_id, label: `${t.term_id} — ${t.name}` })) : [{value:1,label:"1 — 1 Year"},{value:2,label:"2 — 3 Year"},{value:3,label:"3 — Lifetime"},{value:4,label:"4 — Visitor"},{value:5,label:"5 — 20 Year"}]} />
                    </div>
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>Membership Class ID <span className="field-hint-inline">optional — e.g. 2 = ASSOC</span></label>
                        <input type="number" min="0" value={igtForm.igt_membership_class_id ?? ""} onChange={(e) => { const v = e.target.value; setIgtForm({ ...igtForm, igt_membership_class_id: v === "" ? "" : parseInt(v, 10) }); }} placeholder="blank = MeRS default (often ASB)" />
                      </div>
                      <div className="form-field">
                        <label>Invoice Payment Method ID <span className="field-hint-inline">optional — e.g. 6 = STRIPE</span></label>
                        <input type="number" min="0" value={igtForm.igt_payment_method_id ?? ""} onChange={(e) => { const v = e.target.value; setIgtForm({ ...igtForm, igt_payment_method_id: v === "" ? "" : parseInt(v, 10) }); }} placeholder="blank = auto-detect STRIPE" />
                      </div>
                    </div>
                    <div className="info-box" style={{ marginTop: "1rem" }}>
                      {Icon.zap}
                      <span>IGT sessions expire every 90 seconds. The system automatically manages session keepalive via heartbeat.</span>
                    </div>
                  </>
                )}

                {venueForm.provider_type === "swiftpos" && (
                  <>
                    <div className="form-section-label">Endpoint</div>
                    <div className="form-field">
                      <label>API Base URL</label>
                      <input value={swiftposForm[pKey("swiftpos_base_url")] || ""} onChange={(e) => setSwiftposForm({ ...swiftposForm, [pKey("swiftpos_base_url")]: e.target.value })} placeholder="https://api.swiftpos.com.au" />
                    </div>

                    <div className="form-section-label" style={{ marginTop: "0.5rem" }}>Authentication</div>
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>Integrator Name <span className="field-hint-inline">Signature</span></label>
                        <input value={swiftposForm[pKey("swiftpos_integrator_name")] || ""} onChange={(e) => setSwiftposForm({ ...swiftposForm, [pKey("swiftpos_integrator_name")]: e.target.value })} placeholder="e.g. Swift-Circles" />
                      </div>
                      <div className="form-field">
                        <label>Integrator Key <span className="field-hint-inline">Identity</span></label>
                        <SecretInput value={swiftposForm[pKey("swiftpos_integrator_key")] || ""} onChange={(e) => setSwiftposForm({ ...swiftposForm, [pKey("swiftpos_integrator_key")]: e.target.value })} />
                      </div>
                    </div>
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>Client ID</label>
                        <input value={swiftposForm[pKey("swiftpos_client_id")] || ""} onChange={(e) => setSwiftposForm({ ...swiftposForm, [pKey("swiftpos_client_id")]: e.target.value })} />
                      </div>
                      <div className="form-field">
                        <label>Customer ID</label>
                        <input value={swiftposForm[pKey("swiftpos_customer_id")] || ""} onChange={(e) => setSwiftposForm({ ...swiftposForm, [pKey("swiftpos_customer_id")]: e.target.value })} />
                      </div>
                    </div>
                    <div className="form-field">
                      <label>Clerk ID <span className="field-hint-inline">usually 0</span></label>
                      <input value={swiftposForm[pKey("swiftpos_clerk_id")] ?? ""} onChange={(e) => setSwiftposForm({ ...swiftposForm, [pKey("swiftpos_clerk_id")]: e.target.value })} placeholder="0" />
                    </div>

                    <div className="form-section-label" style={{ marginTop: "0.5rem", display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <span>Membership Defaults</span>
                      <button type="button" className="btn btn-outline btn-sm" onClick={handleSwiftposSync} disabled={swiftposSyncing} title="Fetch classifications + countries from SwiftPOS into the local cache">
                        {swiftposSyncing ? "Syncing…" : "Sync from SwiftPOS"}
                      </button>
                    </div>
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>Default Classification <span className="field-hint-inline">membership tier</span></label>
                        {swiftposClassifications.length > 0 ? (
                          <AdminSelect
                            value={swiftposForm.swiftpos_classification_id}
                            onChange={(v) => setSwiftposForm({ ...swiftposForm, swiftpos_classification_id: parseInt(v) })}
                            searchable
                            searchPlaceholder="Search classifications…"
                            options={swiftposClassifications.map(c => ({ value: c.classification_id, label: `${c.classification_id} — ${c.name || "?"}${c.type === 1 ? " (primary)" : ""}` }))}
                          />
                        ) : (
                          <input type="number" min="0" value={swiftposForm.swiftpos_classification_id} onChange={(e) => { const n = parseInt(e.target.value, 10); setSwiftposForm({ ...swiftposForm, swiftpos_classification_id: Number.isNaN(n) ? "" : n }); }} placeholder="e.g. 1 — run Sync to load the list" />
                        )}
                      </div>
                      <div className="form-field">
                        <label>Renewal Term <span className="field-hint-inline">months</span></label>
                        <input type="number" min="1" value={swiftposForm.swiftpos_renewal_months} onChange={(e) => { const n = parseInt(e.target.value, 10); setSwiftposForm({ ...swiftposForm, swiftpos_renewal_months: Number.isNaN(n) ? "" : n }); }} placeholder="12" />
                      </div>
                    </div>
                    <div className="form-section-label" style={{ marginTop: "0.5rem" }}>Self-exclusion</div>
                    <p className="field-hint" style={{ marginTop: "-0.25rem" }}>
                      SwiftPOS has no exclusion API — self-exclusion is a member classification (e.g. "MVRC SOCIAL SELF EXCLUSION"). Pick the classification(s) that mean self-excluded; a member holding one is <strong>blocked</strong> (Full) or <strong>flagged &amp; allowed</strong> (Partial) at online renewal / lookup. The built-in <strong>Banned</strong> classification (99) is <strong>always blocked</strong> regardless of this setting.
                    </p>
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>Treat self-exclusion as</label>
                        <AdminSelect
                          value={swiftposForm[pKey("swiftpos_exclusion_mode")] || "off"}
                          onChange={(v) => setSwiftposForm({ ...swiftposForm, [pKey("swiftpos_exclusion_mode")]: v })}
                          options={[
                            { value: "off", label: "Off — no check" },
                            { value: "full", label: "Full — block online membership" },
                            { value: "partial", label: "Partial — flag + allow" },
                          ]}
                        />
                      </div>
                      <div className="form-field">
                        <label>Self-exclusion classifications</label>
                        {swiftposClassifications.length > 0 ? (
                          <MultiSelect
                            value={String(swiftposForm[pKey("swiftpos_exclusion_classification_ids")] || "").split(",").map((s) => s.trim()).filter(Boolean)}
                            onChange={(arr) => setSwiftposForm({ ...swiftposForm, [pKey("swiftpos_exclusion_classification_ids")]: arr.join(",") })}
                            options={swiftposClassifications.map((c) => ({ value: String(c.classification_id), label: `${c.classification_id} — ${c.name || "?"}` }))}
                            placeholder="None"
                          />
                        ) : (
                          <input value={swiftposForm[pKey("swiftpos_exclusion_classification_ids")] || ""} onChange={(e) => setSwiftposForm({ ...swiftposForm, [pKey("swiftpos_exclusion_classification_ids")]: e.target.value })} placeholder="e.g. 14,99 — Sync to load the list" />
                        )}
                      </div>
                    </div>

                    <div className="info-box" style={{ marginTop: "1rem" }}>
                      {Icon.zap}
                      <span>SwiftPOS assigns the member number on creation. Membership state is the classification + renewal date (a future renewal date = financial) — there's no separate status flag. Membership fees are collected in Stripe; SwiftPOS's own fee/receipt reporting is <strong>not</strong> populated via the API (SwiftPOS has no receipting endpoint). Run "Test Connection" to confirm the classification loads.</span>
                    </div>
                  </>
                )}

                {venueForm.provider_type === "circlescan" && (
                  <>
                    <div className="form-section-label">Endpoint</div>
                    <div className="form-field">
                      <label>API Base URL <span className="field-hint-inline">Circle-provisioned</span></label>
                      <input value={circlescanForm[pKey("circlescan_base_url")] || ""} onChange={(e) => setCirclescanForm({ ...circlescanForm, [pKey("circlescan_base_url")]: e.target.value })} placeholder="https://…azurewebsites.net" />
                    </div>
                    <div className="form-field">
                      <label>API Key <span className="field-hint-inline">sent as Authorization: ApiKey</span></label>
                      <SecretInput value={circlescanForm[pKey("circlescan_api_key")] || ""} onChange={(e) => setCirclescanForm({ ...circlescanForm, [pKey("circlescan_api_key")]: e.target.value })} />
                    </div>

                    <div className="form-section-label" style={{ marginTop: "0.5rem", display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <span>Membership Defaults</span>
                      <button type="button" className="btn btn-outline btn-sm" onClick={handleCirclescanSync} disabled={circlescanSyncing} title="Fetch statuses, types and terms from CircleScan into the local cache">
                        {circlescanSyncing ? "Syncing…" : "Sync from CircleScan"}
                      </button>
                    </div>
                    {(() => {
                      const note = csRefDataNote();
                      if (!note) return null;
                      return (
                        <div className="info-box" style={{ marginBottom: "0.75rem", ...(note.tone === "warn" ? { background: "var(--warning-soft)", color: "var(--warning)" } : {}) }}>
                          {Icon.zap}
                          <span>{note.text}</span>
                        </div>
                      );
                    })()}
                    {(() => {
                      const bad = csInvalidDefaults();
                      if (!bad.length) return null;
                      return (
                        <div className="info-box" style={{ marginBottom: "0.75rem", background: "var(--danger-soft)", color: "var(--danger)" }}>
                          {Icon.zap}
                          <span>
                            Saved {bad.join(" and ")} {bad.length > 1 ? "are" : "is"} not in CircleScan's reference data for this venue —
                            signups will fail with “does not exist”. Pick a value from the list{bad.length > 1 ? "s" : ""} below and save.
                          </span>
                        </div>
                      );
                    })()}
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>Default Status</label>
                        {csStatusOptions().length > 0 ? (
                          <AdminSelect
                            value={circlescanForm[pKey("circlescan_default_status")] || ""}
                            onChange={(v) => setCirclescanForm({ ...circlescanForm, [pKey("circlescan_default_status")]: v })}
                            options={[{ value: "", label: "None" }, ...csStatusOptions()]}
                          />
                        ) : (
                          <input value={circlescanForm[pKey("circlescan_default_status")] || ""} onChange={(e) => setCirclescanForm({ ...circlescanForm, [pKey("circlescan_default_status")]: e.target.value })} placeholder="Type a status code, e.g. FINANCIAL" />
                        )}
                      </div>
                      <div className="form-field">
                        <label>Default Type</label>
                        {csTypeOptions().length > 0 ? (
                          <AdminSelect
                            value={circlescanForm[pKey("circlescan_default_type")] || ""}
                            onChange={(v) => setCirclescanForm({ ...circlescanForm, [pKey("circlescan_default_type")]: v, [pKey("circlescan_default_term_id")]: "" })}
                            options={[{ value: "", label: "None" }, ...csTypeOptions()]}
                          />
                        ) : (
                          <input value={circlescanForm[pKey("circlescan_default_type")] || ""} onChange={(e) => setCirclescanForm({ ...circlescanForm, [pKey("circlescan_default_type")]: e.target.value })} placeholder="Type a member type code, e.g. FULL" />
                        )}
                      </div>
                    </div>
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>Default Term</label>
                        {csTermOptions(circlescanForm[pKey("circlescan_default_type")]).length > 0 ? (
                          <AdminSelect
                            value={circlescanForm[pKey("circlescan_default_term_id")] == null || circlescanForm[pKey("circlescan_default_term_id")] === "" ? "" : String(circlescanForm[pKey("circlescan_default_term_id")])}
                            onChange={(v) => setCirclescanForm({ ...circlescanForm, [pKey("circlescan_default_term_id")]: v === "" ? "" : parseInt(v, 10) })}
                            options={[{ value: "", label: "None" }, ...csTermOptions(circlescanForm[pKey("circlescan_default_type")])]}
                          />
                        ) : (
                          <input type="number" min="0" value={circlescanForm[pKey("circlescan_default_term_id")] ?? ""} onChange={(e) => { const n = parseInt(e.target.value, 10); setCirclescanForm({ ...circlescanForm, [pKey("circlescan_default_term_id")]: Number.isNaN(n) ? "" : n }); }} placeholder="Type a term id, e.g. 1" />
                        )}
                      </div>
                      <div className="form-field">
                        <label>Renewal Term <span className="field-hint-inline">months</span></label>
                        <input type="number" min="1" value={circlescanForm[pKey("circlescan_renewal_months")] ?? ""} onChange={(e) => { const n = parseInt(e.target.value, 10); setCirclescanForm({ ...circlescanForm, [pKey("circlescan_renewal_months")]: Number.isNaN(n) ? "" : n }); }} placeholder="12" />
                      </div>
                    </div>
                    {/* ── Reference data manager — writes straight through to CircleScan ── */}
                    <div className="form-section-label" style={{ marginTop: "1.25rem" }}>Reference Data <span className="field-hint-inline">managed in CircleScan</span></div>
                    <p className="section-desc" style={{ marginTop: 0, marginBottom: "0.75rem" }}>
                      Statuses, member types and terms live in CircleScan. Changes here are saved to CircleScan and re-synced immediately.
                    </p>

                    {["statuses", "types", "terms"].map((kind) => {
                      const rows = circlescanRefData[kind] || [];
                      const meta = CS_REF_META[kind];
                      const typeName = (tid) => {
                        const t = (circlescanRefData.types || []).find((x) => x.type_id === tid);
                        return t ? (t.description || t.code) : (tid != null ? `#${tid}` : "—");
                      };
                      return (
                        <div key={kind} className="cs-ref-block" style={{ marginBottom: "1rem", border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.6rem 0.85rem", background: "var(--bg-elevated)", borderBottom: rows.length ? "1px solid var(--border)" : "none" }}>
                            <span style={{ fontWeight: 600, fontSize: "0.85rem" }}>
                              {meta.plural}
                              <span style={{ color: "var(--text-muted)", fontWeight: 400, marginLeft: "0.4rem" }}>({rows.length})</span>
                            </span>
                            <button type="button" className="btn btn-outline btn-sm" onClick={() => openCsRef(kind)}>+ Add {meta.label}</button>
                          </div>
                          {rows.length === 0 ? (
                            <div style={{ padding: "0.85rem", fontSize: "0.82rem", color: "var(--text-muted)" }}>
                              None configured in CircleScan yet.
                            </div>
                          ) : (
                            // plain scroller — the cs-ref-block above already supplies the
                            // card border + radius; .table-wrap would add a second border
                            <div style={{ overflowX: "auto" }}>
                              <table className="data-table" style={{ fontSize: "0.82rem" }}>
                                <thead>
                                  <tr>
                                    {kind === "terms"
                                      ? <><th>Member Type</th><th>Term</th><th>Rate</th></>
                                      : <><th>Description</th><th>Code</th></>}
                                    <th>Flags</th>
                                    <th style={{ width: 1 }}></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rows.map((r) => {
                                    const rid = kind === "statuses" ? r.status_id : kind === "types" ? r.type_id : r.term_id;
                                    return (
                                      <tr key={rid}>
                                        {kind === "terms" ? (
                                          <>
                                            <td style={{ fontWeight: 600 }}>{typeName(r.type_id)}</td>
                                            <td>{r.term ?? "—"}</td>
                                            <td style={{ color: "var(--text-muted)" }}>{r.rate || "—"}</td>
                                          </>
                                        ) : (
                                          <>
                                            <td style={{ fontWeight: 600 }}>{r.description || "—"}</td>
                                            <td><code style={{ fontSize: "0.78rem", background: "var(--bg-elevated)", padding: "0.1rem 0.4rem", borderRadius: 4 }}>{r.code}</code></td>
                                          </>
                                        )}
                                        <td>
                                          <div style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap" }}>
                                            {r.is_default && <span className="badge active" style={{ fontSize: "0.68rem" }}>Default</span>}
                                            {kind === "statuses" && r.is_valid && <span className="badge active" style={{ fontSize: "0.68rem" }}>Valid</span>}
                                            {kind !== "statuses" && r.is_s2j && <span className="badge active" style={{ fontSize: "0.68rem" }}>S2J</span>}
                                            {r.is_active === false && <span className="badge inactive" style={{ fontSize: "0.68rem" }}>Inactive</span>}
                                          </div>
                                        </td>
                                        <td>
                                          <div style={{ display: "flex", gap: "0.4rem" }}>
                                            <button type="button" className="btn btn-outline btn-sm" onClick={() => openCsRef(kind, r)}>Edit</button>
                                            <button type="button" className="btn btn-danger btn-sm" onClick={() => deleteCsRef(kind, r)}>Delete</button>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    <div className="info-box" style={{ marginTop: "1rem" }}>
                      {Icon.zap}
                      <span>CircleScan uses a static API key (sent as <code>Authorization: ApiKey</code>). It assigns the member number on creation; new members take the status / type / term selected above. Ban, self-exclusion and person-of-interest are checked via CircleScan access control at signup and renewal. Run "Test Connection" to verify.</span>
                    </div>
                  </>
                )}

                <div className="form-actions">
                  <button type="button" className="btn btn-outline" onClick={testConnection} disabled={testingConnection || saving}>
                    {testingConnection ? <><span className="spinner-sm" /> Testing...</> : <>{Icon.zap} Test Connection</>}
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : "Save Provider Config"}</button>
                </div>

                {/* ── Test results panel ─── */}
                {testResults && (
                  <div className={`test-results-panel ${connectionStatus === "ok" ? "success" : "error"}`}>
                    <div className="test-results-header">
                      <span className="test-results-title">
                        {connectionStatus === "ok" ? Icon.check : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>}
                        {testResults.provider} — {connectionStatus === "ok" ? "All checks passed" : "Some checks failed"}
                      </span>
                      <span className="test-results-latency">{testResults.latency}ms</span>
                    </div>
                    <div className="test-results-checks">
                      {testResults.checks.map((c, i) => (
                        <div key={i} className={`test-check-row ${c.status}`}>
                          <span className="test-check-icon">{c.status === "ok" ? Icon.check : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>}</span>
                          <span className="test-check-name">{c.name}</span>
                          <span className="test-check-detail">{c.detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Scheduled member-cache sync (SyncPanel) is S7000/IGT only.
                    SwiftPOS has no bulk member export — its ref data syncs via
                    "Sync from SwiftPOS" above, and members reach the cache via
                    signup / single-import / CSV import (shown below). */}
                {venueForm.provider_type !== "swiftpos" && venueForm.provider_type !== "circlescan" && (
                <div style={{ marginTop: "1.5rem" }}>
                  <div className="form-section-label">Cached data from {venue?.provider_type === "igt" ? "IGT" : "S7000"}</div>
                  <SyncPanel
                    venueId={id}
                    providerType={venue?.provider_type || venueForm.provider_type || "s7000"}
                    syncs={syncStatus}
                    reload={loadSyncStatus}
                    onSyncAll={handleSyncNow}
                    syncingAll={syncing}
                  />
                </div>
                )}

                {/* ── Import a single member (all providers) ─── */}
                <div style={{ marginTop: "1.5rem" }}>
                  <div className="form-section-label">Import a single member</div>
                  <p className="section-desc" style={{ marginTop: 0 }}>
                    {(venue?.provider_type || "s7000") === "igt"
                      ? "Fetches the member's profile from MeRS and caches it locally. IGT has no bulk list, so this is the only way to add members to the cache."
                      : venue?.provider_type === "swiftpos"
                      ? "Fetches the member from SwiftPOS by member id and caches it locally. SwiftPOS has no bulk list, so this + CSV import are how members reach the cache."
                      : "Refresh one member's cache entry by membership number or card number. Useful for on-demand fixes without waiting for the next scheduled sync."}
                  </p>
                    {importMemberMsg && (
                      <div className={`toast ${importMemberMsg.ok ? "toast-success" : "toast-error"}`} style={{ marginBottom: "0.75rem" }}>
                        {importMemberMsg.text}
                      </div>
                    )}
                  <form onSubmit={importMemberNow} style={{ display: "flex", gap: "0.5rem", alignItems: "flex-end", maxWidth: 520 }}>
                    <div className="form-field" style={{ flex: 1, marginBottom: 0 }}>
                      <label>Membership or card number</label>
                      <input
                        value={importMemberInput}
                        onChange={(e) => setImportMemberInput(e.target.value.replace(/\D/g, ""))}
                        placeholder="e.g. 56153"
                        inputMode="numeric"
                      />
                    </div>
                    <button type="submit" className="btn btn-primary" disabled={importMemberBusy || !importMemberInput}>
                      {importMemberBusy ? "Importing…" : "Import"}
                    </button>
                  </form>
                </div>

              </form>
            )}

            {activeSection === "tunnel" && venueForm.provider_type === "igt" && (
              <IgtTunnelPanel venueId={id} venueName={venue?.name} onChanged={() => loadVenue()} />
            )}
            {activeSection === "tunnel" && venueForm.provider_type === "s7000" && (
              <S7000TunnelPanel venueId={id} venueName={venue?.name} onChanged={() => loadVenue()} />
            )}

            {activeSection === "stripe" && (() => {
              // Pick which key set the form binds to based on the sub-tab.
              const isSandbox = stripeEnv === "sandbox";
              const keys = isSandbox
                ? { pub: "sandbox_stripe_publishable_key", sec: "sandbox_stripe_secret_key" }
                : { pub: "stripe_publishable_key",         sec: "stripe_secret_key"         };
              const placeholderPrefix = isSandbox ? "test_" : "live_";
              return (
                <form className="form-section" onSubmit={saveStripe}>
                  <div className="section-header-row">
                    <div>
                      <h2>Stripe Configuration</h2>
                      <p className="section-desc">Payment processing for this venue</p>
                    </div>
                    {stripeConfigured && <span className="connection-badge connected">{Icon.check} Configured</span>}
                  </div>
                  <EnvironmentSubTabs value={stripeEnv} onChange={setStripeEnv} />
                  <div className="form-field">
                    <label>Publishable Key</label>
                    <span className="field-hint">Stripe Dashboard → Developers → API keys (starts with pk_)</span>
                    <input value={stripeForm[keys.pub]} onChange={(e) => setStripeForm({ ...stripeForm, [keys.pub]: e.target.value })} placeholder={`pk_${placeholderPrefix}...`} />
                  </div>
                  <div className="form-field">
                    <label>Secret Key</label>
                    <span className="field-hint">Stripe Dashboard → Developers → API keys (starts with sk_)</span>
                    <SecretInput value={stripeForm[keys.sec]} onChange={(e) => setStripeForm({ ...stripeForm, [keys.sec]: e.target.value })} placeholder={`sk_${placeholderPrefix}...`} />
                  </div>
                  <div className="info-box" style={{ marginTop: "0.5rem" }}>
                    {Icon.zap}
                    <span>Payments are verified server-side using the secret key — no webhook setup required.</span>
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? "Saving…" : `Save ${isSandbox ? "Sandbox" : "Live"} Stripe Config`}
                    </button>
                  </div>
                </form>
              );
            })()}

            {activeSection === "email-provider" && (
              <EmailProviderPanel venueId={Number(id)} onFlash={(msg, type) => type === "error" ? setError(msg) : setSuccess(msg)} />
            )}

            {activeSection === "emails" && (
              <EmailsTab venueId={Number(id)} venue={venue} />
            )}
          </div>
        </div>
      )}

      {/* ══ Members Tab ══════════════════════════════════════ */}
      {tab === "members" && (
        <AdminMembersTab venueId={id} providerType={venue?.provider_type || "s7000"} />
      )}

      {/* ══ Logs Tab ══════════════════════════════════════ */}
      {tab === "logs" && (
        <div>
          <div className="venues-summary">
            <div className="venues-summary-counts">
              <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700 }}>Activity Logs</h2>
              <span>{logs.length} entries</span>
            </div>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Action</th>
                  <th>Member</th>
                  <th>Status</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr><td colSpan="5" style={{ textAlign: "center", padding: "3rem", color: "var(--text-muted)" }}>No activity yet</td></tr>
                ) : logs.map((l) => (
                  <tr key={l.id}>
                    <td style={{ fontSize: "0.875rem", color: "var(--text-muted)" }}>{new Date(l.created_at).toLocaleString()}</td>
                    <td style={{ fontWeight: 500 }}>{l.action}</td>
                    <td>{l.first_name} {l.last_name}</td>
                    <td><span className={`badge ${l.status}`}>{l.status}</span></td>
                    <td style={{ fontWeight: 600 }}>{l.amount_cents ? `$${(l.amount_cents / 100).toFixed(2)}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══ Clients Tab ══════════════════════════════════════ */}
      {tab === "clients" && (
        <div>
          {clients.length === 0 ? (
            <div className="empty-state">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4-4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
              <h3>No client users</h3>
              <p>Invite people to manage this venue's members via the Venue Portal</p>
              <button className="btn btn-primary" onClick={() => setShowInvite(true)}>+ Invite Client</button>
            </div>
          ) : (
            <>
              <div className="tab-section-header">
                <div>
                  <h2 className="tab-section-title">Client Users</h2>
                  <p className="tab-section-desc">{clients.length} user{clients.length !== 1 ? "s" : ""} with portal access</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowInvite(true)}>+ Invite Client</button>
              </div>
              <div className="card-list">
                {clients.map((c) => (
                  <div key={c.id} className="card-list-item">
                    <div className="card-list-avatar">{(c.name || c.email)?.charAt(0)?.toUpperCase()}</div>
                    <div className="card-list-info">
                      <span className="card-list-name">{c.name || "Unnamed"}</span>
                      <span className="card-list-sub">{c.email}</span>
                    </div>
                    <span className="badge active">Active</span>
                    <div className="card-list-actions">
                      <button className="btn btn-outline btn-sm" onClick={() => openResetPw(c)}>Reset Password</button>
                      <button className="btn btn-danger btn-sm" onClick={() => deleteClient(c.id)}>Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Reset Password Modal */}
          {showResetPw && resetPwTarget && (
            <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowResetPw(false)}>
              <div className="modal">
                <div className="modal-header">
                  <div className="modal-header-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  </div>
                  <div className="modal-header-text">
                    <h2>Reset Password</h2>
                    <p>{resetPwTarget.name || resetPwTarget.email}</p>
                  </div>
                  <button type="button" className="modal-close-btn" onClick={() => setShowResetPw(false)} aria-label="Close">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
                  </button>
                </div>
                <form onSubmit={resetClientPassword}>
                  <div className="modal-body">
                    <div className="form-field">
                      <label>New Password <span className="required">*</span></label>
                      <PasswordInput
                        value={resetPwVal}
                        onChange={(e) => setResetPwVal(e.target.value)}
                        placeholder="Minimum 6 characters"
                        required
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="btn btn-ghost" onClick={() => setShowResetPw(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={savingResetPw}>{savingResetPw ? "Saving..." : "Set Password"}</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Invite Modal */}
          {showInvite && (
            <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowInvite(false)}>
              <div className="modal">
                <div className="modal-header">
                  <div className="modal-header-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
                  </div>
                  <div className="modal-header-text">
                    <h2>Invite Client User</h2>
                    <p>Create portal access for {venue.name}</p>
                  </div>
                  <button type="button" className="modal-close-btn" onClick={() => setShowInvite(false)} aria-label="Close">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
                  </button>
                </div>
                <form onSubmit={addClient}>
                  <div className="modal-body">
                    <div className="form-field">
                      <label>Full Name</label>
                      <input value={clientForm.name} onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })} placeholder="John Smith" />
                    </div>
                    <div className="form-field">
                      <label>Email <span className="required">*</span></label>
                      <input type="email" value={clientForm.email} onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })} placeholder="john@example.com" required />
                    </div>
                    <div className="form-field">
                      <label>Temporary Password <span className="required">*</span></label>
                      <PasswordInput value={clientForm.password} onChange={(e) => setClientForm({ ...clientForm, password: e.target.value })} placeholder="Minimum 6 characters" required />
                    </div>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="btn btn-ghost" onClick={() => setShowInvite(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Create &amp; Invite</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ Products Tab ══════════════════════════════════════ */}
      {tab === "products" && (
        <div>
          {products.length === 0 ? (
            <div className="empty-state">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>
              <h3>No products yet</h3>
              <p>Add membership plans that members can purchase during sign-up</p>
              <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
                <button className="btn btn-primary" onClick={openAddProduct}>+ Add Product</button>
                {venueForm.provider_type === "igt" && (
                  <button className="btn btn-outline" onClick={openImportWizard} style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>{Icon.zap} Import from IGT</button>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="tab-section-header">
                <div>
                  <h2 className="tab-section-title">Membership Products</h2>
                  <p className="tab-section-desc">{products.length} plan{products.length !== 1 ? "s" : ""} available on the signup page</p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  {venueForm.provider_type === "igt" && (
                    <button className="btn btn-outline" onClick={openImportWizard} style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>{Icon.zap} Import from IGT</button>
                  )}
                  <button className="btn btn-primary" onClick={openAddProduct}>+ Add Product</button>
                </div>
              </div>
              <div className="table-wrap">
                <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Price</th>
                    <th>Duration</th>
                    <th>Stripe</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{p.name}</div>
                        {p.description && <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "0.15rem" }}>{p.description}</div>}
                      </td>
                      <td style={{ fontWeight: 700 }}>${(p.price_cents / 100).toFixed(2)} <span style={{ fontSize: "0.8rem", fontWeight: 400, color: "var(--text-muted)" }}>{(p.currency || "AUD").toUpperCase()}</span></td>
                      <td style={{ color: "var(--text-muted)", fontSize: "0.875rem" }}>{p.duration_months === 1200 ? "Lifetime" : (p.duration_months ? `${p.duration_months} month${p.duration_months !== 1 ? "s" : ""}` : "—")}</td>
                      <td>{p.stripe_price_id ? <span className="badge active">Synced</span> : <span className="badge inactive">Local</span>}</td>
                      <td><span className={`badge ${p.active ? "active" : "inactive"}`}>{p.active ? "Active" : "Inactive"}</span></td>
                      <td>
                        <div style={{ display: "flex", gap: "0.5rem" }}>
                          <button className="btn btn-outline btn-sm" onClick={() => openEditProduct(p)}>Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => deleteProduct(p.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}


          {/* Product Modal */}
          {showProductModal && (
            <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowProductModal(false)}>
              <div className="modal modal-wide">
                <div className="modal-header">
                  <div className="modal-header-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
                  </div>
                  <div className="modal-header-text">
                    <h2>{editProduct ? "Edit Product" : "Add Membership Product"}</h2>
                    <p>{editProduct ? "Update product details below" : "Create a new membership tier for this venue"}</p>
                  </div>
                  <button type="button" className="modal-close-btn" onClick={() => setShowProductModal(false)} aria-label="Close">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
                  </button>
                </div>
                <form onSubmit={saveProduct}>
                  <div className="modal-body">
                    <div className="form-field">
                      <label>Product name <span className="required">*</span></label>
                      <input value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} placeholder="e.g. Gold Annual Membership" required />
                    </div>
                    <div className="form-field">
                      <label>Description <span className="optional">— optional</span></label>
                      <input value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} placeholder="Brief description shown to members" />
                    </div>
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>Price <span className="required">*</span></label>
                        <div className="input-currency-wrap">
                          <span className="input-prefix">$</span>
                          <input type="number" step="0.01" min="0" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} placeholder="0.00" required />
                          <span className="input-suffix">AUD</span>
                        </div>
                      </div>
                      <div className="form-field">
                        <label>Duration <span className="required">*</span></label>
                        <AdminSelect value={productForm.duration_months} onChange={(v) => setProductForm({ ...productForm, duration_months: parseInt(v) })} options={[{value:1,label:"1 month"},{value:3,label:"3 months"},{value:6,label:"6 months"},{value:12,label:"12 months (1 year)"},{value:24,label:"24 months (2 years)"},{value:36,label:"36 months (3 years)"},{value:60,label:"60 months (5 years)"},{value:1200,label:"Lifetime (100 years)"}]} />
                        <span className="field-hint">Expiry duration sent to venue provider</span>
                      </div>
                    </div>
                    {/* ── Analysis codes per product (S7000 only) ── */}
                    {venueForm.provider_type === "s7000" && analysisTypes.length > 0 && (() => {
                      // Group all types for value lookups
                      const typeMap = {};
                      for (const t of analysisTypes) {
                        if (!typeMap[t.typecode]) typeMap[t.typecode] = { name: t.name, values: [] };
                        typeMap[t.typecode].values.push({ valuecode: t.valuecode, label: t.value_label });
                      }
                      const typeOptions = Object.entries(typeMap).sort((a, b) => {
                        const na = parseInt(a[0]), nb = parseInt(b[0]);
                        if (!isNaN(na) && !isNaN(nb)) return na - nb;
                        return a[0].localeCompare(b[0]);
                      });
                      const codes = productForm.analysis_codes || [];
                      const mappedTypecodes = new Set(codes.map(c => c.typecode));
                      const unmappedTypes = typeOptions.filter(([tc]) => !mappedTypecodes.has(tc));

                      const addMapping = (typecode) => {
                        const info = typeMap[typecode];
                        if (!info) return;
                        setProductForm({ ...productForm, analysis_codes: [...codes, { typecode, valuecode: info.values[0]?.valuecode || "" }] });
                      };
                      const updateMapping = (typecode, valuecode) => {
                        setProductForm({ ...productForm, analysis_codes: codes.map(c => c.typecode === typecode ? { ...c, valuecode } : c) });
                      };
                      const removeMapping = (typecode) => {
                        setProductForm({ ...productForm, analysis_codes: codes.filter(c => c.typecode !== typecode) });
                      };

                      return (
                        <div style={{ marginTop: "0.75rem", padding: "1rem", background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: 10 }}>
                          <div style={{ marginBottom: 10 }}>
                            <label style={{ fontWeight: 600, fontSize: "0.88rem", display: "block", color: "var(--text)" }}>Analysis Codes</label>
                            <span className="field-hint">When a member picks this product, these values are sent to S7000. Example: set MEMBER TYPE to "SILVER" for the Silver product.</span>
                          </div>

                          {codes.length > 0 && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
                              {codes.map(c => {
                                const info = typeMap[c.typecode] || { name: c.typecode, values: [] };
                                return (
                                  <div key={c.typecode} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: 8 }}>
                                    <div style={{ minWidth: 140 }}>
                                      <span style={{ fontWeight: 600, fontSize: "0.82rem", color: "var(--text)" }}>
                                        <code style={{ color: "var(--accent)", fontSize: "0.75rem", marginRight: 4 }}>{c.typecode}</code>
                                        {info.name}
                                      </span>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <AdminSelect
                                        value={c.valuecode}
                                        onChange={v => updateMapping(c.typecode, v)}
                                        options={info.values.map(v => ({ value: v.valuecode, label: `${v.valuecode} — ${v.label || "(no label)"}` }))}
                                        placeholder="Pick a value..."
                                      />
                                    </div>
                                    <button type="button" onClick={() => removeMapping(c.typecode)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 4, fontSize: "1.1rem", lineHeight: 1 }} title="Remove mapping">✕</button>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {codes.length === 0 && (
                            <p style={{ fontSize: "0.82rem", color: "var(--text-muted)", margin: "0 0 12px", fontStyle: "italic" }}>No analysis codes mapped yet. Add one below.</p>
                          )}

                          {unmappedTypes.length > 0 && (
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <AdminSelect
                                value=""
                                onChange={v => { if (v) addMapping(v); }}
                                options={unmappedTypes.map(([tc, info]) => ({ value: tc, label: `${tc} — ${info.name}` }))}
                                placeholder="+ Add analysis code..."
                              />
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* ── IGT mapping (IGT venues only) ──
                        Each venue product maps to an IGT membership-type
                        + term. buildIgtMemberBody uses these when
                        building the Member Create body, falling back
                        to the venue-level IGT defaults when a product
                        isn't mapped. */}
                    {venueForm.provider_type === "igt" && (
                      <div style={{ marginTop: "0.75rem", padding: "1rem", background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: 10 }}>
                        <div style={{ marginBottom: 10 }}>
                          <label style={{ fontWeight: 600, fontSize: "0.88rem", display: "block", color: "var(--text)" }}>IGT mapping</label>
                          <span className="field-hint">
                            Pick which IGT membership type + term this product registers members under.
                            Both are required — products must be self-contained, no venue-default fallback.
                          </span>
                        </div>
                        <div className="form-row-2">
                          <div className="form-field">
                            <label>Membership type</label>
                            <AdminSelect
                              value={productForm.igt_membership_type_id}
                              onChange={(v) => setProductForm({ ...productForm, igt_membership_type_id: v ? Number(v) : "" })}
                              options={igtTypes.map((t) => ({ value: t.id, label: t.name + (t.isPrimary ? " (primary)" : "") }))}
                              placeholder="Select membership type"
                            />
                          </div>
                          <div className="form-field">
                            <label>Term</label>
                            <AdminSelect
                              value={productForm.igt_term_id}
                              onChange={(v) => setProductForm({ ...productForm, igt_term_id: v ? Number(v) : "" })}
                              options={igtTerms.map((t) => ({ value: t.id, label: t.name }))}
                              placeholder="Select term"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {venueForm.provider_type === "swiftpos" && (
                      <div style={{ marginTop: "0.75rem", padding: "1rem", background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: 10 }}>
                        <div style={{ marginBottom: 10 }}>
                          <label style={{ fontWeight: 600, fontSize: "0.88rem", display: "block", color: "var(--text)" }}>SwiftPOS classification</label>
                          <span className="field-hint">
                            Which SwiftPOS classification members on this product join. Selecting one fills the price from its SwiftPOS membership fee (editable) — Stripe syncs to match. Leave blank to use the venue's Default Classification.
                          </span>
                        </div>
                        <div className="form-field">
                          <label>Classification</label>
                          {swiftposClassifications.length > 0 ? (
                            <AdminSelect
                              value={productForm.swiftpos_classification_id}
                              onChange={(v) => {
                                const cid = v ? Number(v) : "";
                                const cls = swiftposClassifications.find((c) => c.classification_id === cid);
                                setProductForm((f) => ({
                                  ...f,
                                  swiftpos_classification_id: cid,
                                  // Drive the price from the SwiftPOS classification fee (still editable).
                                  ...(cls && cls.membership_fee != null ? { price: Number(cls.membership_fee).toFixed(2) } : {}),
                                }));
                              }}
                              searchable
                              searchPlaceholder="Search classifications…"
                              options={[{ value: "", label: "Use venue default" }, ...swiftposClassifications.map((c) => ({ value: c.classification_id, label: `${c.classification_id} — ${c.name || "?"}${c.membership_fee != null ? ` · $${Number(c.membership_fee).toFixed(2)}` : ""}${c.type === 1 ? " (primary)" : ""}` }))]}
                              placeholder="Use venue default"
                            />
                          ) : (
                            <input type="number" min="0" value={productForm.swiftpos_classification_id} onChange={(e) => { const n = parseInt(e.target.value, 10); setProductForm({ ...productForm, swiftpos_classification_id: Number.isNaN(n) ? "" : n }); }} placeholder="Blank = venue default · Sync on the Provider tab to load the list" />
                          )}
                          {(() => {
                            const cls = swiftposClassifications.find((c) => c.classification_id === productForm.swiftpos_classification_id);
                            if (cls && cls.membership_fee != null) {
                              return <span className="field-hint">SwiftPOS membership fee for {cls.name}: <strong>${Number(cls.membership_fee).toFixed(2)}</strong>{Number(cls.membership_fee) === 0 ? " (sandbox classifications have no fee set)" : ""}</span>;
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    )}

                    {venueForm.provider_type === "circlescan" && (
                      <div style={{ marginTop: "0.75rem", padding: "1rem", background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: 10 }}>
                        <div style={{ marginBottom: 10 }}>
                          <label style={{ fontWeight: 600, fontSize: "0.88rem", display: "block", color: "var(--text)" }}>CircleScan membership</label>
                          <span className="field-hint">
                            Which CircleScan member type + term members on this product join. Leave blank to use the venue's Default Type / Term. Status defaults from the venue config.
                          </span>
                        </div>
                        <div className="form-grid-2">
                          <div className="form-field">
                            <label>Member Type</label>
                            {csTypeOptions().length > 0 ? (
                              <AdminSelect
                                value={productForm.circlescan_type || ""}
                                onChange={(v) => setProductForm((f) => ({ ...f, circlescan_type: v, circlescan_term_id: "" }))}
                                options={[{ value: "", label: "Use venue default" }, ...csTypeOptions()]}
                                placeholder="Use venue default"
                              />
                            ) : (
                              <input value={productForm.circlescan_type || ""} onChange={(e) => setProductForm({ ...productForm, circlescan_type: e.target.value })} placeholder="Blank = venue default · or type a code" />
                            )}
                          </div>
                          <div className="form-field">
                            <label>Term</label>
                            {csTermOptions(productForm.circlescan_type).length > 0 ? (
                              <AdminSelect
                                value={productForm.circlescan_term_id == null || productForm.circlescan_term_id === "" ? "" : String(productForm.circlescan_term_id)}
                                onChange={(v) => setProductForm({ ...productForm, circlescan_term_id: v === "" ? "" : parseInt(v, 10) })}
                                options={[{ value: "", label: "Use venue default" }, ...csTermOptions(productForm.circlescan_type)]}
                                placeholder="Use venue default"
                              />
                            ) : (
                              <input type="number" min="0" value={productForm.circlescan_term_id ?? ""} onChange={(e) => { const n = parseInt(e.target.value, 10); setProductForm({ ...productForm, circlescan_term_id: Number.isNaN(n) ? "" : n }); }} placeholder="Blank = venue default · or type an id" />
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <label className="toggle-row" htmlFor="syncStripe">
                      <div className="toggle-row-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/><path d="m9 12 2 2 4-4"/></svg>
                      </div>
                      <div className="toggle-row-text">
                        <strong>Sync to Stripe</strong>
                        <span>Create or update this product in your Stripe account</span>
                      </div>
                      <div className="toggle-switch">
                        <input type="checkbox" id="syncStripe" checked={productForm.sync_to_stripe} onChange={(e) => setProductForm({ ...productForm, sync_to_stripe: e.target.checked })} />
                        <span className="toggle-track" />
                      </div>
                    </label>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="btn btn-outline" onClick={() => setShowProductModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={savingProduct}>{savingProduct ? "Saving..." : editProduct ? "Update product" : "Create product"}</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ── IGT Import Wizard ── */}
          {showImportWizard && (
            <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && importStep !== 3 && closeImportWizard()}>
              <div className="iw">
                {/* Header */}
                <div className="iw-header">
                  <div className="iw-header-left">
                    <div className="iw-icon">{Icon.zap}</div>
                    <div>
                      <div className="iw-title">Import from IGT</div>
                      <div className="iw-subtitle">
                        {importStep === 0 && "Connecting…"}
                        {importStep === 1 && "Select membership products to import"}
                        {importStep === 2 && "Configure pricing for selected products"}
                        {importStep === 3 && "Creating products…"}
                        {importStep === 4 && "Import complete"}
                      </div>
                    </div>
                  </div>
                  <div className="iw-header-right">
                    {importStep >= 1 && importStep <= 2 && (
                      <div className="iw-steps">
                        <div className={`iw-dot${importStep >= 1 ? " active" : ""}`} />
                        <div className="iw-dot-line" />
                        <div className={`iw-dot${importStep >= 2 ? " active" : ""}`} />
                      </div>
                    )}
                    {importStep !== 3 && (
                      <button type="button" className="iw-close" onClick={closeImportWizard} aria-label="Close">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* Body */}
                <div className="iw-body">
                  {/* Loading */}
                  {importStep === 0 && (
                    <div className="iw-center">
                      <div className="spinner-sm" style={{ width: 28, height: 28 }} />
                    </div>
                  )}

                  {/* Step 1 — Select */}
                  {importStep === 1 && importError && !importCandidates.length && (
                    <div className="test-results-panel error" style={{ margin: 0 }}><p style={{ margin: 0 }}>{importError}</p></div>
                  )}

                  {importStep === 1 && importCandidates.length > 0 && (<>
                    {importError && <div className="test-results-panel error" style={{ marginBottom: "0.75rem", padding: "0.4rem 0.6rem", fontSize: "0.82rem" }}><p style={{ margin: 0 }}>{importError}</p></div>}
                    {igtTypes.map(t => {
                      const tc = importCandidates.filter(c => c.typeId === t.id);
                      const selN = tc.filter(c => c.selected && !c.alreadyExists).length;
                      const avN = tc.filter(c => !c.alreadyExists).length;
                      return (
                        <div key={t.id} className="iw-type">
                          <div className="iw-type-row">
                            <span className="iw-type-name">{t.name}</span>
                            {t.isPrimary && <span className="badge active" style={{ fontSize: "0.58rem", padding: "0.05rem 0.3rem", lineHeight: 1.4 }}>Primary</span>}
                            {avN > 0 && <button type="button" className="iw-link" onClick={() => toggleTypeAllTerms(t.id)}>{selN === avN ? "Clear" : "Select all"}</button>}
                          </div>
                          <div className="iw-chips">
                            {tc.map(c => (
                              <button key={c.key} type="button" disabled={c.alreadyExists}
                                className={`iw-chip${c.selected ? " on" : ""}${c.alreadyExists ? " done" : ""}`}
                                onClick={() => !c.alreadyExists && toggleCandidate(c.key)}>
                                {c.termName}
                                <span className="iw-chip-dur">{c.duration_months ? `${c.duration_months}mo` : "∞"}</span>
                                {c.alreadyExists && <span className="iw-chip-check">✓</span>}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </>)}

                  {/* Step 2 — Pricing */}
                  {importStep === 2 && (<>
                    {importError && <div className="test-results-panel error" style={{ marginBottom: "0.75rem", padding: "0.4rem 0.6rem", fontSize: "0.82rem" }}><p style={{ margin: 0 }}>{importError}</p></div>}
                    <table className="iw-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th style={{ width: 110 }}>Price (AUD)</th>
                          <th style={{ width: 90 }}>Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {igtTypes.map(t => {
                          const sel = importCandidates.filter(c => c.typeId === t.id && c.selected && !c.alreadyExists);
                          if (!sel.length) return null;
                          return (
                            <React.Fragment key={t.id}>
                              <tr className="iw-table-group"><td colSpan={3}>{t.name}</td></tr>
                              {sel.map(c => (
                                <tr key={c.key}>
                                  <td>
                                    <div className="iw-table-product">
                                      <span className="iw-table-term">{c.termName}</span>
                                      <input className="iw-table-input iw-table-name" value={c.name} onChange={(e) => setCandidateField(c.key, "name", e.target.value)} />
                                    </div>
                                  </td>
                                  <td>
                                    <div className="iw-table-price">
                                      <span className="iw-table-dollar">$</span>
                                      <input className="iw-table-input" type="number" step="0.01" min="0" value={c.price} onChange={(e) => setCandidateField(c.key, "price", e.target.value)} placeholder="0.00" />
                                    </div>
                                  </td>
                                  <td>
                                    <div className="iw-table-dur">
                                      <input className="iw-table-input" type="number" min="0" value={c.duration_months} onChange={(e) => setCandidateField(c.key, "duration_months", parseInt(e.target.value) || 0)} />
                                      <span className="iw-table-mo">mo</span>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </>)}

                  {/* Step 3 — Creating */}
                  {importStep === 3 && (
                    <div className="iw-center">
                      <div className="spinner-sm" style={{ width: 28, height: 28 }} />
                      <p style={{ color: "var(--text-muted)", marginTop: "0.75rem" }}>Creating {importCandidates.filter(c => c.selected && !c.alreadyExists).length} products…</p>
                    </div>
                  )}

                  {/* Step 4 — Done */}
                  {importStep === 4 && (
                    <div>
                      <div className={`test-results-panel ${importResults.every(r => r.status === "ok") ? "success" : "error"}`} style={{ marginBottom: "0.75rem" }}>
                        <div className="test-results-header">
                          {importResults.filter(r => r.status === "ok").length} of {importResults.length} created
                        </div>
                      </div>
                      <div className="import-results-list">
                        {importResults.map((r, i) => (
                          <div key={i} className={`test-check-row ${r.status}`}>
                            <span className="test-check-icon">{r.status === "ok" ? Icon.check : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>}</span>
                            <span className="test-check-name">{r.name}</span>
                            <span className="test-check-detail">{r.detail}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="iw-footer">
                  {importStep === 1 && importCandidates.length > 0 && (<>
                    <span className="iw-footer-count">{importCandidates.filter(c => c.selected && !c.alreadyExists).length} selected</span>
                    <div className="iw-footer-actions">
                      <button type="button" className="btn btn-ghost" onClick={closeImportWizard}>Cancel</button>
                      <button type="button" className="btn btn-primary" onClick={goToPricing} disabled={!importCandidates.some(c => c.selected && !c.alreadyExists)}>
                        Next: Pricing →
                      </button>
                    </div>
                  </>)}
                  {importStep === 1 && !importCandidates.length && (
                    <div className="iw-footer-actions"><button type="button" className="btn btn-ghost" onClick={closeImportWizard}>Close</button></div>
                  )}
                  {importStep === 2 && (<>
                    <span className="iw-footer-count">{importCandidates.filter(c => c.selected && !c.alreadyExists).length} product{importCandidates.filter(c => c.selected && !c.alreadyExists).length !== 1 ? "s" : ""}</span>
                    <div className="iw-footer-actions">
                      <button type="button" className="btn btn-ghost" onClick={() => { setImportStep(1); setImportError(""); }}>← Back</button>
                      <button type="button" className="btn btn-primary" onClick={executeImport}>
                        Create Products
                      </button>
                    </div>
                  </>)}
                  {importStep === 4 && (
                    <div className="iw-footer-actions"><button type="button" className="btn btn-primary" onClick={closeImportWizard}>Done</button></div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ Digital Card Tab ══════════════════════════════════ */}
      {tab === "digitalcard" && (
        <DigitalCardTab venueId={id} venueName={venue.name} providerType={venue.provider_type || "s7000"} litecardConfig={venue.litecardConfig} />
      )}

      {/* ══ Licence Capture (FOXID) Tab ═══════════════════════ */}
      {tab === "foxid" && (
        <FoxidConfigTab venueId={id} initial={venue.foxidConfig} />
      )}

      {/* ══ API Tab ═══════════════════════════════════════════ */}
      {tab === "api" && <ApiKeysTab venueId={id} venueSlug={venueForm.slug} />}

      {/* ══ Integration Tests Tab ═════════════════════════════ */}
      {tab === "integration-tests" && <IntegrationTestsTab venueId={id} />}

      {/* ══ Analysis Codes Tab ════════════════════════════════ */}
      {tab === "analysis" && (
        <AnalysisCodesTab venueId={id} providerType={venueForm.provider_type} />
      )}

      {/* CircleScan reference-data Modal — page level, NOT inside a tab block:
          it is opened from the Settings → Provider tab, so nesting it in another
          tab leaves it unmounted and the Add/Edit buttons do nothing. */}
      {csRefModal && (() => {
        const { kind, item } = csRefModal;
        const meta = CS_REF_META[kind];
        const set = (patch) => setCsRefForm((f) => ({ ...f, ...patch }));
        return (
          <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setCsRefModal(null)}>
            <div className="modal">
              <div className="modal-header">
                <div className="modal-header-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
                </div>
                <div className="modal-header-text">
                  <h2>{item ? `Edit ${meta.label}` : `Add ${meta.label}`}</h2>
                  <p>Saved directly to CircleScan for this venue</p>
                </div>
                <button type="button" className="modal-close-btn" onClick={() => setCsRefModal(null)} aria-label="Close">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
                </button>
              </div>
              <form onSubmit={saveCsRef}>
                <div className="modal-body">
                  {/* Errors surface here — the page-level toast sits behind the
                      modal overlay, so a failed save would otherwise look silent. */}
                  {csRefError && <div className="toast toast-error" style={{ marginBottom: "0.75rem" }}>{csRefError}</div>}

                  {kind === "terms" ? (
                    <>
                      <div className="form-field">
                        <label>Member type <span className="required">*</span></label>
                        <AdminSelect
                          value={csRefForm.type_id === "" || csRefForm.type_id == null ? "" : String(csRefForm.type_id)}
                          onChange={(v) => set({ type_id: v === "" ? "" : parseInt(v, 10) })}
                          options={(circlescanRefData.types || []).map((t) => ({ value: String(t.type_id), label: `${t.description || t.code} (${t.code})` }))}
                          placeholder="Select a member type…"
                        />
                      </div>
                      <div className="form-grid-2">
                        <div className="form-field">
                          <label>Term <span className="required">*</span></label>
                          <input type="number" min="0" value={csRefForm.term ?? ""} onChange={(e) => { const n = parseInt(e.target.value, 10); set({ term: Number.isNaN(n) ? "" : n }); }} placeholder="e.g. 3" required />
                          <span className="field-hint">Length as CircleScan defines it for this venue</span>
                        </div>
                        <div className="form-field">
                          <label>Rate <span className="optional">— optional</span></label>
                          <input value={csRefForm.rate || ""} onChange={(e) => set({ rate: e.target.value })} placeholder="e.g. $18" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="form-field">
                        <label>Code <span className="required">*</span></label>
                        <input value={csRefForm.code || ""} onChange={(e) => set({ code: e.target.value })} placeholder={kind === "statuses" ? "e.g. 1" : "e.g. SOC"} required />
                        <span className="field-hint">The value stored on the member — CircleScan matches on this exactly.</span>
                      </div>
                      <div className="form-field">
                        <label>Description <span className="optional">— optional</span></label>
                        <input value={csRefForm.description || ""} onChange={(e) => set({ description: e.target.value })} placeholder={kind === "statuses" ? "e.g. Financial" : "e.g. Social"} />
                        <span className="field-hint">Shown in the portal instead of the raw code.</span>
                      </div>
                    </>
                  )}

                  {kind === "statuses" && (
                    <ToggleRow id="csref-valid" label="Counts as a valid membership" hint="Members on this status are treated as financial"
                      checked={csRefForm.is_valid} onChange={(v) => set({ is_valid: v })} />
                  )}
                  {kind !== "statuses" && (
                    <ToggleRow id="csref-s2j" label="Available for Scan2Join" hint="Offered in CircleScan's scan-to-join flow"
                      checked={csRefForm.is_s2j} onChange={(v) => set({ is_s2j: v })} />
                  )}
                  <ToggleRow id="csref-default" label="Default" hint={`Used when a ${meta.label.toLowerCase()} isn't specified`}
                    checked={csRefForm.is_default} onChange={(v) => set({ is_default: v })} />
                  <ToggleRow id="csref-active" label="Active" hint="Inactive items stay on record but can't be selected"
                    checked={csRefForm.is_active} onChange={(v) => set({ is_active: v })} />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setCsRefModal(null)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={csRefSaving}>
                    {csRefSaving ? "Saving…" : item ? "Save Changes" : `Add ${meta.label}`}
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}
    </AdminLayout>
  );
}
